import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Box,
    Typography,
    LinearProgress,
    Chip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    useMediaQuery,
    useTheme as useMuiTheme,
} from "@mui/material";
import UserAvatar from "../shared/UserAvatar";
import {
    Star as StarIcon,
    AddCircleOutlineOutlined as BonusIcon,
    Shield as ShieldIcon,
    Whatshot as FireIcon,
    Lock as LockIcon,
    LockOpen as LockOpenIcon,
    SupervisorAccount as ApprovalIcon,
    Check as CheckIcon,
    PanTool as RequestIcon,
    Delete as DeleteIcon,
    Undo as UndoIcon,
    Restore as RestoreIcon,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import api from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import { useAppSettings } from "../../contexts/AppSettingsContext";
import TaskCompletionAnimation from "../shared/TaskCompletionAnimation";
import LottieTreasureChest from "../shared/LottieTreasureChest";
import LottieGiftReveal from "../shared/LottieGiftReveal";
import AchievementStack from "../shared/AchievementStack";
import GradualBlur from "../shared/GradualBlur";
import LiveTimer from "../shared/LiveTimer";
import { getCurrentDate } from "../../utils/dateUtils";
import { useThemeMode } from "../../contexts/ThemeContext";
import {
    THEMES,
    DEFAULT_THEME,
    DEFAULT_SHAPE,
    contrast,
} from "../../theme/themes";
import { statusColors, buildModePalette } from "../../theme/tokens/base";
import {
    AuroraShader,
    Galaxy,
    ProfileEffectOverlay,
    RainbowBorder,
    AmbientParticles,
} from "../effects";
import TaskFormDrawer from "../shared/TaskFormDrawer";
import { useToast } from "../../contexts/ToastContext";

const REFRESH_INTERVAL = 30000;

const PIXIE_DUST_EFFECT = {
    name: "Pixie Dust",
    slug: "pixie-dust",
    type: "profile-effect",
    width: 450,
    height: 880,
    effects: [
        {
            src: "/uploads/profile-effects/pixie-dust/layer-0.png",
            loop: true,
            start: 0,
            duration: 2880,
            zIndex: 100,
            position: { x: 0, y: 0 },
            loopDelay: 2880,
            width: 450,
            height: 880,
        },
    ],
    reducedMotionSrc: "/uploads/profile-effects/pixie-dust/static.png",
};

const COLUMN_COLORS = [
    DEFAULT_THEME,
    THEMES.purple,
    THEMES.green,
    THEMES.orange,
];

const getChildTheme = (child, idx, mode) => {
    const key = child.theme?.toLowerCase();
    const isDark = mode === "dark";
    const t =
        key && THEMES[key]
            ? THEMES[key]
            : COLUMN_COLORS[idx % COLUMN_COLORS.length];
    const effectiveDark = t.isDarkTheme || isDark;
    const modeKey = effectiveDark ? "dark" : "light";

    // Generate defaults from core colors
    const defaults = buildModePalette(t, modeKey);

    // Merge theme's mode-specific overrides on top of defaults
    const modeOverrides = t.modes?.[modeKey] || {};

    // Nameplate: new format { light: {...}, dark: {...} } or legacy { src: "..." }
    let nameplate = null;
    if (t.nameplate) {
        if (t.nameplate[modeKey]) {
            nameplate = t.nameplate[modeKey];
        } else if (t.nameplate.src) {
            nameplate = t.nameplate;
        }
    }

    return {
        ...defaults,
        ...modeOverrides,
        // Always available
        accent: t.main,
        main: t.main,
        light: t.light,
        dark: t.dark,
        avatarBg: modeOverrides.avatarBg || t.main,
        contrastText: t.contrastText || "#fff",
        isSuper: t.isSuper || false,
        effectiveDark,
        // Pass-through (non-mode-specific)
        effects: t.effects || null,
        typography: t.typography || null,
        shapes: t.shapes || DEFAULT_SHAPE,
        textures: t.textures || null,
        overlays: t.overlays || null,
        animations: t.animations || null,
        components: t.components || null,
        shape: t.shape || null,
        nameplate,
        profileHeader: t.profileHeader || null,
        defaultBackground: t.defaultBackground || null,
    };
};

const LEVEL_NAMES = {
    1: "Ensiaskel",
    2: "Ahkera Apuri",
    3: "Kotitöiden Sankari",
    4: "Supersankari",
    5: "Mestari",
    6: "Legenda",
    7: "Kotitöiden Jumala",
};

const LEVEL_THRESHOLDS = [0, 50, 150, 350, 700, 1200, 2000];

const KioskDashboard = () => {
    const { mode } = useThemeMode();
    const { user, isKioskContext } = useAuth();
    const { requireTaskConfirm } = useAppSettings();
    const { showToast } = useToast();
    const muiTheme = useMuiTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
    const isDark = mode === "dark";
    const [children, setChildren] = useState([]);
    const [loading, setLoading] = useState(true);
    const [celebration, setCelebration] = useState(null);
    const [celebrationKey, setCelebrationKey] = useState(0);
    const [bonusOpen, setBonusOpen] = useState(null);
    const [bonusTasks, setBonusTasks] = useState([]);
    const [achievementQueues, setAchievementQueues] = useState({});
    const [achievementStackVisible, setAchievementStackVisible] = useState({});
    const [pendingLevelUp, setPendingLevelUp] = useState(null);
    const [levelUp, setLevelUp] = useState(null);
    const [shieldInfoOpen, setShieldInfoOpen] = useState(false);
    const [shieldChildId, setShieldChildId] = useState(null);
    const [purchasingFreeze, setPurchasingFreeze] = useState(false);
    const [taskDrawerOpen, setTaskDrawerOpen] = useState(false);
    const [hideCompleted, setHideCompleted] = useState(true);
    const [taskRequestDialog, setTaskRequestDialog] = useState(null); // childId
    const [taskRequestMessage, setTaskRequestMessage] = useState("");
    const [taskRequestSent, setTaskRequestSent] = useState({});
    const [undoConfirm, setUndoConfirm] = useState(null);
    const lastLoadDate = React.useRef(new Date().toISOString().split("T")[0]);
    const taskRefs = useRef({});
    const containerRef = useRef(null);
    const [linkLines, setLinkLines] = useState([]);
    const [clipRect, setClipRect] = useState(null);
    const [nameplateData, setNameplateData] = useState({});
    const [interactionLocked, setInteractionLocked] = useState(
        () => localStorage.getItem("kiosk_interaction_locked") === "true",
    );
    const [dismissedTasks, setDismissedTasks] = useState({});
    // Task confirmation state: { taskId, childId, task, accentColor, goalAchieved, colors }
    const [pendingConfirm, setPendingConfirm] = useState(null);

    const loadData = useCallback(async () => {
        try {
            const today = new Date().toISOString().split("T")[0];
            if (today !== lastLoadDate.current) {
                lastLoadDate.current = today;
                setChildren([]);
            }
            const res = await api.get("/kiosk/today");
            setChildren(res.data);
        } catch (err) {
            console.error("Kiosk load error:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
        const interval = setInterval(loadData, REFRESH_INTERVAL);
        return () => clearInterval(interval);
    }, [loadData]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const toggleInteractionLock = useCallback(() => {
        setInteractionLocked((prev) => {
            const next = !prev;
            localStorage.setItem("kiosk_interaction_locked", String(next));
            return next;
        });
    }, []);

    const getDismissKey = useCallback(
        (userId) =>
            `dismissed_tasks_${userId}_${new Date().toISOString().split("T")[0]}`,
        [],
    );

    useEffect(() => {
        if (!children.length) return;
        const map = {};
        children.forEach((c) => {
            const stored = JSON.parse(
                localStorage.getItem(getDismissKey(c.id)) || "[]",
            );
            map[c.id] = new Set(stored);
        });
        setDismissedTasks(map);
    }, [children, getDismissKey]);

    const dismissTask = useCallback(
        (childId, taskId) => {
            setDismissedTasks((prev) => {
                const childSet = new Set(prev[childId] || []);
                childSet.add(taskId);
                localStorage.setItem(
                    getDismissKey(childId),
                    JSON.stringify([...childSet]),
                );
                return { ...prev, [childId]: childSet };
            });
            setPendingConfirm(null);
        },
        [getDismissKey],
    );

    const restoreTask = useCallback(
        (childId, taskId) => {
            setDismissedTasks((prev) => {
                const childSet = new Set(prev[childId] || []);
                childSet.delete(taskId);
                localStorage.setItem(
                    getDismissKey(childId),
                    JSON.stringify([...childSet]),
                );
                return { ...prev, [childId]: childSet };
            });
        },
        [getDismissKey],
    );

    const handleUndoCompletion = useCallback(
        async (taskId, childId, taskTitle) => {
            if (interactionLocked) return;
            setUndoConfirm({ taskId, childId, taskTitle });
        },
        [interactionLocked],
    );

    const confirmUndoCompletion = useCallback(async () => {
        if (!undoConfirm) return;
        try {
            await api.undoTaskCompletion(
                undoConfirm.taskId,
                undoConfirm.childId,
            );
            await loadData();
        } catch (error) {
            console.error("Undo completion error:", error);
        } finally {
            setUndoConfirm(null);
        }
    }, [undoConfirm, loadData]);

    // Fetch nameplate data for themes that have them or child's task_animation
    useEffect(() => {
        children.forEach((child, idx) => {
            const colors = getChildTheme(child, idx, mode);
            // Prefer child's task_animation over theme's nameplate
            let npSrc = null;
            if (child.task_animation) {
                npSrc = `/uploads/profile-effects/${child.task_animation}/bg.json`;
            } else if (colors.nameplate?.src) {
                npSrc = colors.nameplate.src;
            }
            if (npSrc && !nameplateData[npSrc]) {
                fetch(npSrc)
                    .then((r) => r.json())
                    .then((data) => {
                        setNameplateData((prev) => ({
                            ...prev,
                            [npSrc]: data,
                        }));
                    })
                    .catch((err) =>
                        console.error("Failed to load nameplate:", err),
                    );
            }
            // Also prefetch the dark nameplate if present
            const npDarkSrc = colors.nameplateDark?.src;
            if (npDarkSrc && !nameplateData[npDarkSrc]) {
                fetch(npDarkSrc)
                    .then((r) => r.json())
                    .then((data) => {
                        setNameplateData((prev) => ({
                            ...prev,
                            [npDarkSrc]: data,
                        }));
                    })
                    .catch((err) =>
                        console.error("Failed to load dark nameplate:", err),
                    );
            }
        });
    }, [children, nameplateData]);

    // Preload task completion effect APNG layers so they're cached when animation triggers
    useEffect(() => {
        children.forEach((child) => {
            if (!child.task_effect) return;
            const jsonUrl = `/uploads/profile-effects/${child.task_effect}/effect.json`;
            fetch(jsonUrl)
                .then((r) => r.json())
                .then((data) => {
                    if (data?.effects) {
                        data.effects.forEach((layer) => {
                            if (layer.src) {
                                const img = new Image();
                                img.src = layer.src;
                            }
                        });
                    }
                })
                .catch(() => {});
        });
    }, [children]);

    // Handler for shared task completion (used by both direct WS and window event)
    const handleSharedTaskCompleted = useCallback(
        (taskId, completedBy, completedByName) => {
            console.log("Processing shared_task_completed:", {
                taskId,
                completedBy,
                completedByName,
            });
            setChildren((prev) =>
                prev.map((child) => ({
                    ...child,
                    tasks: child.tasks.map((t) =>
                        Number(t.id) === Number(taskId)
                            ? Number(child.id) === Number(completedBy)
                                ? { ...t, completed: true }
                                : {
                                      ...t,
                                      completedByOther: true,
                                      completedByName,
                                  }
                            : t,
                    ),
                })),
            );
        },
        [],
    );

    // Listen for window event from WebSocketContext (when logged in as kioski)
    useEffect(() => {
        const onWindowEvent = (e) => {
            const { taskId, completedBy, completedByName } = e.detail;
            handleSharedTaskCompleted(taskId, completedBy, completedByName);
        };
        window.addEventListener("sharedTaskCompleted", onWindowEvent);
        return () =>
            window.removeEventListener("sharedTaskCompleted", onWindowEvent);
    }, [handleSharedTaskCompleted]);

    // Direct WebSocket connection for kiosk (fallback for public/unauthenticated access)
    useEffect(() => {
        // Use same origin — nginx/NPM proxies /ws to backend
        const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
        const wsUrl = `${protocol}//${window.location.host}/ws`;
        let ws = null;
        let reconnectTimeout = null;

        const connect = () => {
            try {
                console.log("Kiosk connecting to WebSocket:", wsUrl);
                ws = new WebSocket(wsUrl);

                ws.onopen = () => {
                    console.log("Kiosk WebSocket connected");
                    ws.send(
                        JSON.stringify({
                            type: "auth",
                            userId: "kiosk",
                            userType: "kiosk",
                        }),
                    );
                };

                ws.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);
                        if (data.type === "shared_task_completed") {
                            const { taskId, completedBy, completedByName } =
                                data;
                            handleSharedTaskCompleted(
                                taskId,
                                completedBy,
                                completedByName,
                            );
                        }
                        if (
                            data.type === "gift_received" ||
                            data.type === "SHARED_GOAL_REWARDED" ||
                            data.type === "task_completion_undone"
                        ) {
                            loadData();
                        }
                    } catch (err) {
                        console.error("Kiosk WS message error:", err);
                    }
                };

                ws.onclose = (event) => {
                    if (event.code !== 1000) {
                        reconnectTimeout = setTimeout(connect, 3000);
                    }
                };

                ws.onerror = () => {};
            } catch (err) {
                console.error("Kiosk WS connect error:", err);
            }
        };

        connect();

        return () => {
            if (reconnectTimeout) clearTimeout(reconnectTimeout);
            if (ws) ws.close(1000);
        };
    }, [handleSharedTaskCompleted]);

    // Calculate link lines for shared tasks
    const calculateLinks = useCallback(() => {
        if (!containerRef.current || children.length !== 2) {
            setLinkLines([]);
            setClipRect(null);
            return;
        }

        const links = [];
        const sharedTaskIds = new Set();
        const containerRect = containerRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        // Compute clip bounds from sticky header bottom and bonus area top
        const headerEl = containerRef.current.querySelector(
            "[data-sticky-header]",
        );
        const bonusEl = containerRef.current.querySelector("[data-bonus-area]");
        const clipTop = headerEl
            ? headerEl.getBoundingClientRect().bottom - containerRect.top
            : 0;
        const clipBottom = bonusEl
            ? bonusEl.getBoundingClientRect().top - containerRect.top
            : viewportHeight;
        setClipRect({ top: clipTop, bottom: clipBottom });

        // Find tasks that appear in multiple children's lists (shared tasks, not bonus)
        children.forEach((child) => {
            child.tasks.forEach((task) => {
                if (
                    (!task.assigned_to || task.assigned_to === "") &&
                    !task.is_bonus
                ) {
                    sharedTaskIds.add(task.id);
                }
            });
        });

        // For each shared task, find positions across columns
        sharedTaskIds.forEach((taskId) => {
            const positions = [];
            children.forEach((child, childIdx) => {
                const task = child.tasks.find((t) => t.id === taskId);
                if (task) {
                    const refKey = `${child.id}-${taskId}`;
                    const el = taskRefs.current[refKey];
                    if (el) {
                        const rect = el.getBoundingClientRect();
                        const y =
                            rect.top + rect.height / 2 - containerRect.top;

                        // Only include if within reasonable viewport bounds (with buffer)
                        if (y >= -100 && y <= viewportHeight + 100) {
                            positions.push({
                                childIdx,
                                childId: child.id,
                                // Right edge for left task, left edge for right task
                                xRight: rect.right - containerRect.left,
                                xLeft: rect.left - containerRect.left,
                                y: Math.max(0, Math.min(viewportHeight, y)),
                                color: getChildTheme(child, childIdx, mode)
                                    .accent,
                                completed: task.completed,
                            });
                        }
                    }
                }
            });

            // Draw lines between consecutive positions (right edge to left edge)
            for (let i = 0; i < positions.length - 1; i++) {
                const p1 = positions[i];
                const p2 = positions[i + 1];
                const midX = (p1.xRight + p2.xLeft) / 2;
                links.push({
                    taskId: `${taskId}-${i}`,
                    x1: p1.xRight,
                    y1: p1.y,
                    x2: p2.xLeft,
                    y2: p2.y,
                    cx1: midX,
                    cx2: midX,
                    color: p1.completed || p2.completed ? "#9e9e9e" : "#e91e63",
                    completed: p1.completed || p2.completed,
                });
            }
        });

        setLinkLines(links);
    }, [children]);

    useEffect(() => {
        // Delay calculation to allow DOM to settle
        const timer = setTimeout(calculateLinks, 100);
        return () => clearTimeout(timer);
    }, [children, calculateLinks]);

    // Recalculate lines on scroll
    useEffect(() => {
        if (!containerRef.current || children.length !== 2) return;

        const scrollableElements =
            containerRef.current.querySelectorAll("[data-task-scroll]");
        let rafId = null;

        const handleScroll = () => {
            if (rafId) return;
            rafId = requestAnimationFrame(() => {
                calculateLinks();
                rafId = null;
            });
        };

        scrollableElements.forEach((el) => {
            el.addEventListener("scroll", handleScroll, { passive: true });
        });

        return () => {
            scrollableElements.forEach((el) => {
                el.removeEventListener("scroll", handleScroll);
            });
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
        };
    }, [children, calculateLinks]);

    const handleBuyFreeze = async (childId) => {
        if (interactionLocked) return;
        if (purchasingFreeze) return;

        const child = children.find((c) => c.id === childId);
        if (!child) return;

        const freezePrice = child.freezePrice || 12;

        if (child.coins < freezePrice) {
            showToast(`Ei tarpeeksi kolikoita! Tarvitset ${freezePrice} kolikkoa.`, 'warning');
            return;
        }

        setPurchasingFreeze(true);
        try {
            await api.post("/shop/purchase/streak-freeze", {
                userId: childId,
            });

            // Refresh data to show updated freeze_available and coins
            await loadData();

            setShieldInfoOpen(false);
            setShieldChildId(null);
        } catch (error) {
            console.error("Buy freeze error:", error);
            const errorMsg = error.response?.data?.error || "Osto epäonnistui";
            showToast(errorMsg, 'error');
            // Don't close dialog on error - let user see the error and try again or close manually
        } finally {
            setPurchasingFreeze(false);
        }
    };

    const handleComplete = async (
        taskId,
        userId,
        task,
        accentColor,
        showCoins = false,
        childTheme = null,
    ) => {
        if (interactionLocked) return;
        // Only block if task is explicitly locked (outside time window)
        if (
            task.timeWindowStatus === "locked_future" ||
            task.timeWindowStatus === "locked_past"
        ) {
            return;
        }

        // Skip if animation already playing (cosmetic only, task is already submitted)
        if (celebration) return;

        try {
            const res = await api.post("/kiosk/complete", { taskId, userId });

            // Show different celebration based on approval status
            if (res.data.needsApproval) {
                setCelebration({
                    task: {
                        title: task.title,
                        reward_points: 0,
                        icon: task.icon,
                    },
                    childId: userId,
                    accentColor,
                    showCoins: false,
                    pendingApproval: true,
                    theme: childTheme,
                    userEffect: children.find((c) => c.id === userId)
                        ?.task_effect,
                });
                setCelebrationKey((k) => k + 1);
            } else {
                setCelebration({
                    task: {
                        title: task.title,
                        reward_points: task.reward_points,
                        icon: task.icon,
                    },
                    childId: userId,
                    accentColor,
                    showCoins,
                    theme: childTheme,
                    userEffect: children.find((c) => c.id === userId)
                        ?.task_effect,
                });
                setCelebrationKey((k) => k + 1);

                if (res.data.leveledUp) {
                    setPendingLevelUp({
                        childId: userId,
                        newLevel: res.data.level,
                        accentColor,
                    });
                }

                if (res.data.newAchievements?.length > 0) {
                    setAchievementQueues((prev) => ({
                        ...prev,
                        [userId]: [
                            ...(prev[userId] || []),
                            ...res.data.newAchievements,
                        ],
                    }));
                    setAchievementStackVisible((prev) => ({
                        ...prev,
                        [userId]: true,
                    }));
                }
            }

            loadData();
        } catch (err) {
            if (err.response?.data?.outsideWindow) {
                console.log("Outside time window");
            } else {
                console.error("Complete error:", err);
            }
        }
    };

    // gameRules.md: Lapsi ei voi peruuttaa tehtyä tehtävää
    // Peruutus on vain vanhemman oikeus

    const openBonus = async (childId) => {
        try {
            const res = await api.get(`/bonus-tasks?userId=${childId}`);
            setBonusTasks(res.data);
            setBonusOpen(childId);
        } catch (err) {
            console.error("Bonus load error:", err);
            setBonusTasks([]);
            setBonusOpen(childId);
        }
    };

    const claimBonus = async (bonusTaskId, childId) => {
        if (interactionLocked) return;
        try {
            await api.post(`/bonus-tasks/${bonusTaskId}/claim`, {
                userId: childId,
            });
            setBonusOpen(null);
            // Remove claimed bonus from list and update child's bonus count locally
            setBonusTasks((prev) => prev.filter((bt) => bt.id !== bonusTaskId));
            setChildren((prev) =>
                prev.map((c) =>
                    c.id === childId
                        ? {
                              ...c,
                              availableBonusCount: Math.max(
                                  0,
                                  (c.availableBonusCount || 0) - 1,
                              ),
                          }
                        : c,
                ),
            );
            // Notify MobileNav to update badge immediately
            window.dispatchEvent(
                new CustomEvent("bonus-claimed", {
                    detail: { userId: childId },
                }),
            );
            loadData();
        } catch (err) {
            console.error("Claim bonus error:", err);
        }
    };

    const handleMoneyBagComplete = async (childId) => {
        if (interactionLocked) return;
        try {
            await api.post("/kiosk/open-bag", { userId: childId });
        } catch {}
        loadData();
    };

    const handleMondayTopUpComplete = async (childId) => {
        if (interactionLocked) return;
        try {
            await api.post("/kiosk/open-monday-topup", { userId: childId });
        } catch {}
        loadData();
    };

    const handleGiftOpened = async (childId, notificationId) => {
        if (interactionLocked) return;
        try {
            await api.post("/kiosk/open-notification", {
                userId: childId,
                notificationId,
                type: "gift_received",
            });
        } catch {}
        loadData();
    };

    const handleGoalRewardOpened = async (childId, notificationId) => {
        if (interactionLocked) return;
        try {
            await api.post("/kiosk/open-notification", {
                userId: childId,
                notificationId,
                type: "shared_goal_rewarded",
            });
        } catch {}
        loadData();
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "100dvh",
                }}
            >
                <Typography variant="h5">Ladataan...</Typography>
            </Box>
        );
    }

    // Filter to only show logged-in child's column when user is a child (but not in kiosk context)
    const displayChildren =
        user?.type === "child" && !isKioskContext
            ? children.filter((c) => c.id === user.id)
            : children;

    // Single child mobile view (child logged in on mobile)
    const isSingleChildMobile =
        isMobile && displayChildren.length === 1 && user?.type === "child";

    if (displayChildren.length === 0) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "100dvh",
                    flexDirection: "column",
                    gap: 3,
                }}
            >
                <Typography variant="h5" color="text.secondary">
                    Ei käyttäjiä lisätty. Kirjaudu vanhempana lisätäksesi
                    käyttäjiä.
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            ref={containerRef}
            sx={{
                display: "flex",
                height: isSingleChildMobile
                    ? "100dvh"
                    : isMobile
                      ? "auto"
                      : "100dvh",
                overflowY: isSingleChildMobile ? "auto" : "hidden",
                overflowX: isMobile ? "hidden" : "auto",
                flexDirection: isMobile ? "column" : "row",
                position: "relative",
            }}
        >
            {user?.type === "parent" && (
                <IconButton
                    onClick={toggleInteractionLock}
                    sx={{
                        position: "fixed",
                        top: 12,
                        right: 12,
                        zIndex: 1100,
                        bgcolor: interactionLocked
                            ? "rgba(244,67,54,0.15)"
                            : "rgba(0,0,0,0.05)",
                        "&:hover": {
                            bgcolor: interactionLocked
                                ? "rgba(244,67,54,0.25)"
                                : "rgba(0,0,0,0.1)",
                        },
                    }}
                >
                    {interactionLocked ? (
                        <LockIcon color="error" />
                    ) : (
                        <LockOpenIcon />
                    )}
                </IconButton>
            )}
            {/* SVG overlay for shared task link lines — desktop only */}
            {linkLines.length > 0 && (
                <Box
                    sx={{
                        display: { xs: "none", md: "block" },
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        pointerEvents: "none",
                        zIndex: 5,
                    }}
                >
                    <svg
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            pointerEvents: "none",
                        }}
                    >
                        <defs>
                            <clipPath id="task-area-clip">
                                <rect
                                    x={0}
                                    y={clipRect ? clipRect.top : 0}
                                    width="100%"
                                    height={
                                        clipRect
                                            ? Math.max(
                                                  0,
                                                  clipRect.bottom -
                                                      clipRect.top,
                                              )
                                            : "100%"
                                    }
                                />
                            </clipPath>
                        </defs>
                        <g clipPath="url(#task-area-clip)">
                            {linkLines.map((link) => (
                                <path
                                    key={link.taskId}
                                    d={`M ${link.x1} ${link.y1} C ${link.cx1} ${link.y1}, ${link.cx2} ${link.y2}, ${link.x2} ${link.y2}`}
                                    stroke={link.color}
                                    strokeWidth={5}
                                    fill="none"
                                    strokeLinecap="round"
                                    opacity={link.completed ? 0.3 : 0.6}
                                />
                            ))}
                        </g>
                    </svg>
                </Box>
            )}
            {displayChildren.map((child, idx) => {
                const colors = getChildTheme(child, idx, mode);
                const hasBgImage = !!(
                    child.background_url || colors.defaultBackground
                );
                const effectiveCardBg =
                    hasBgImage && colors.effectiveDark
                        ? "rgba(20, 20, 20, 0.85)"
                        : colors.cardBg;
                const bgTextColor = hasBgImage
                    ? colors.effectiveDark
                        ? "#ffffffed"
                        : "#fff"
                    : undefined;
                const bgTextShadow = hasBgImage
                    ? "0 1px 3px rgba(0,0,0,0.7)"
                    : "none";

                // Split tasks into normal (active) and scheduled (waiting/expired) zones
                const normalTasks = [];
                const scheduledTasks = [];
                const now = getCurrentDate();

                child.tasks.forEach((task) => {
                    const hasTimeWindow = task.time_start && task.time_end;
                    const isCompleted = task.completed || task.completedByOther;

                    if (hasTimeWindow && !isCompleted) {
                        const [sh, sm] = task.time_start.split(":").map(Number);
                        const [eh, em] = task.time_end.split(":").map(Number);
                        const startDate = new Date(
                            now.getFullYear(),
                            now.getMonth(),
                            now.getDate(),
                            sh,
                            sm,
                            0,
                        );
                        const endDate = new Date(
                            now.getFullYear(),
                            now.getMonth(),
                            now.getDate(),
                            eh,
                            em,
                            0,
                        );
                        const isActive = now >= startDate && now <= endDate;

                        if (isActive) {
                            normalTasks.push(task);
                        } else {
                            scheduledTasks.push(task);
                        }
                    } else {
                        normalTasks.push(task);
                    }
                });

                // Sort: incomplete tasks first, completed last
                normalTasks.sort((a, b) => {
                    const aComplete = a.completed || a.completedByOther ? 1 : 0;
                    const bComplete = b.completed || b.completedByOther ? 1 : 0;
                    return aComplete - bComplete;
                });

                // Only count normal tasks (exclude scheduled/expired from counter)
                const activeTasks = normalTasks.filter(
                    (t) => !t.completedByOther,
                );
                const completed = activeTasks.filter((t) => t.completed).length;
                const total = activeTasks.length;
                const remaining = total - completed;
                const isBonusOpen = bonusOpen === child.id;
                const level = child.level || 1;
                const totalXp = child.totalXp || 0;
                const currentLevelXp = LEVEL_THRESHOLDS[level - 1] || 0;
                const nextLevelXp = LEVEL_THRESHOLDS[level] || 999999;
                const xpProgress =
                    nextLevelXp > currentLevelXp
                        ? Math.min(
                              100,
                              Math.round(
                                  ((totalXp - currentLevelXp) /
                                      (nextLevelXp - currentLevelXp)) *
                                      100,
                              ),
                          )
                        : 100;
                const hasPendingConversion =
                    child.conversionStatus === "pending" &&
                    child.conversionCoins > 0;
                const hasUnopenedConversion =
                    child.conversionStatus === "ready" &&
                    !child.conversionOpened;
                const hasMondayTopUp = (child.pending_monday_topup || 0) > 0;
                const hasPendingGift = !!child.pendingGift;
                const hasPendingGoalReward = !!child.pendingGoalReward;

                // Weekly goal progress
                const weeklyGoal = child.weeklyGoal || 100;
                const weeklyPoints = child.weeklyPoints || 0;
                const goalPct = Math.min(
                    100,
                    Math.round((weeklyPoints / weeklyGoal) * 100),
                );
                const goalAchieved = weeklyPoints >= weeklyGoal;

                // Get contrast-safe colors for this child's theme
                const effectiveDark = colors.effectiveDark;

                return (
                    <Box
                        key={child.id}
                        sx={{
                            flex: 1,
                            minWidth: isMobile ? 0 : 380,
                            maxHeight: isSingleChildMobile
                                ? "none"
                                : isMobile
                                  ? "none"
                                  : "100dvh",
                            overflow: isSingleChildMobile
                                ? "visible"
                                : isMobile
                                  ? "visible"
                                  : "hidden",
                            bgcolor: colors.bg,
                            // Background: layer image over gradient, or just gradient
                            ...(child.background_url || colors.defaultBackground
                                ? {
                                      backgroundImage: `url(${child.background_url || colors.defaultBackground})`,
                                      backgroundSize: "cover",
                                      backgroundPosition: "center",
                                      backgroundRepeat: "no-repeat",
                                      backgroundColor: colors.bg,
                                  }
                                : {
                                      background:
                                          colors.background || colors.bg,
                                  }),
                            display: "flex",
                            flexDirection: "column",
                            width: isMobile ? "100%" : undefined,
                            borderRight: isMobile
                                ? "none"
                                : idx < displayChildren.length - 1
                                  ? colors.borderColumn
                                  : "none",
                            borderBottom:
                                isMobile && idx < displayChildren.length - 1
                                    ? colors.borderColumn
                                    : "none",
                            minHeight: isSingleChildMobile
                                ? `calc(100dvh - 56px - env(safe-area-inset-bottom, 0px))`
                                : isMobile
                                  ? "auto"
                                  : "100dvh",
                            position: "relative",
                            // Theme-specific top glow bar
                            ...(colors.effects?.topGlowBar && {
                                "&::before": {
                                    content: '""',
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: "3px",
                                    background: `linear-gradient(90deg, transparent, ${colors.accent}, ${colors.effects?.accentGlow || colors.accent}, ${colors.accent}, transparent)`,
                                    opacity: 0.7,
                                    zIndex: 20,
                                },
                            }),
                        }}
                    >
                        {/* Scanlines overlay for themes that define it */}
                        {colors.overlays?.scanlines && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    background: `repeating-linear-gradient(
                    0deg,
                    ${colors.overlays.scanlineColor || "rgba(0,0,0,0.1)"},
                    ${colors.overlays.scanlineColor || "rgba(0,0,0,0.1)"} 1px,
                    transparent 1px,
                    transparent ${colors.overlays.scanlineGap || "2px"}
                  )`,
                                    pointerEvents: "none",
                                    zIndex: 10,
                                }}
                            />
                        )}
                        {/* Gradient overlay for background images - improves text readability */}
                        {child.background_url && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: "35%",
                                    background: colors.backgroundOverlay,
                                    pointerEvents: "none",
                                    zIndex: 0,
                                }}
                            />
                        )}
                        {/* Aurora shader background for themes that define it */}
                        {colors.effects?.aurora && (
                            <>
                                <AuroraShader
                                    colorStops={
                                        colors.effects.auroraColorStops || [
                                            "#5227FF",
                                            "#7cff67",
                                            "#5227FF",
                                        ]
                                    }
                                    amplitude={
                                        colors.effects.auroraAmplitude ?? 1.0
                                    }
                                    blend={colors.effects.auroraBlend ?? 0.5}
                                    speed={colors.effects.auroraSpeed ?? 1.0}
                                    opacity={
                                        colors.effects.auroraOpacity ?? 0.6
                                    }
                                />
                                {/* Gradient overlay to fade aurora from top to bottom */}
                                <Box
                                    sx={{
                                        position: "absolute",
                                        inset: 0,
                                        background:
                                            "linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.2) 40%, rgba(0,0,0,0) 100%)",
                                        pointerEvents: "none",
                                        zIndex: 0,
                                    }}
                                />
                            </>
                        )}
                        {/* Galaxy shader background for themes that define it */}
                        {colors.effects?.galaxy && (
                            <Galaxy
                                mouseRepulsion={false}
                                mouseInteraction={false}
                                density={colors.effects.galaxyDensity ?? 0.2}
                                glowIntensity={
                                    colors.effects.galaxyGlowIntensity ?? 0.2
                                }
                                saturation={
                                    colors.effects.galaxySaturation ?? 0
                                }
                                hueShift={colors.effects.galaxyHueShift ?? 140}
                                twinkleIntensity={
                                    colors.effects.galaxyTwinkleIntensity ?? 0.3
                                }
                                rotationSpeed={
                                    colors.effects.galaxyRotationSpeed ?? 0
                                }
                                starSpeed={
                                    colors.effects.galaxyStarSpeed ?? 0.9
                                }
                                speed={colors.effects.galaxySpeed ?? 0.3}
                                transparent={true}
                            />
                        )}
                        {/* Ambient particles for themes that define it */}
                        {colors.animations?.ambientParticles && (
                            <AmbientParticles />
                        )}
                        {/* Child header - sticky on mobile */}
                        <Box
                            data-sticky-header
                            sx={{
                                p: 2.5,
                                position: isMobile ? "sticky" : "relative",
                                top: isMobile ? 0 : undefined,
                                zIndex: isMobile ? 10 : 1,
                                flexShrink: 0,
                            }}
                        >
                            {/* Avatar + Name row */}
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 2.5,
                                    mb: 1.5,
                                }}
                            >
                                {/* Avatar */}
                                <UserAvatar
                                    src={child.avatar_url}
                                    frame={child.avatar_frame}
                                    size={120}
                                    bgcolor={colors.avatarBg}
                                    animation={colors.animations?.avatar}
                                    glowColor={colors.effects?.glowColor}
                                    sx={{
                                        fontSize: "2.2rem",
                                        fontWeight: 900,
                                        border:
                                            colors.animations?.avatar ===
                                            "rainbowBorder"
                                                ? undefined
                                                : colors.avatarBorder,
                                        boxShadow: `0 4px 16px ${colors.accent}44`,
                                    }}
                                >
                                    {child.name.charAt(0)}
                                </UserAvatar>

                                {/* Name + Chips */}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    {/* Child name */}
                                    <Typography
                                        variant="h5"
                                        fontWeight={900}
                                        sx={{
                                            color: colors.palette?.retro
                                                ? colors.palette.retro.textWhite
                                                : colors.accent,
                                            textShadow: colors.palette?.retro
                                                ? colors.effects.textShadowHard
                                                : "none",
                                            lineHeight: 1.1,
                                            fontFamily:
                                                colors.typography?.headline ||
                                                "inherit",
                                            mb: 0.75,
                                        }}
                                    >
                                        {child.name}
                                    </Typography>

                                    {/* Chips row: Level, Streak, Freeze - uniform sizing */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.75,
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        <Chip
                                            label={
                                                isMobile
                                                    ? LEVEL_NAMES[level] || ""
                                                    : `Taso ${level}: ${LEVEL_NAMES[level] || ""}`
                                            }
                                            size="small"
                                            sx={{
                                                bgcolor: colors.palette?.retro
                                                    ? colors.palette.retro
                                                          .levelBg
                                                    : `${colors.accent}22`,
                                                color: colors.palette?.retro
                                                    ? colors.palette.retro
                                                          .textWhite
                                                    : colors.accent,
                                                fontWeight:
                                                    colors.effects
                                                        ?.fontWeightPixel ||
                                                    700,
                                                fontSize: "0.7rem",
                                                height: 26,
                                                fontFamily:
                                                    colors.typography?.mono ||
                                                    "inherit",
                                                borderRadius:
                                                    colors.components?.chip
                                                        ?.borderRadius ?? 50,
                                                border:
                                                    colors.components?.chip
                                                        ?.border || "none",
                                                textShadow: colors.palette
                                                    ?.retro
                                                    ? colors.effects
                                                          .textShadowHard
                                                    : "none",
                                            }}
                                        />
                                        {child.currentStreak >= 1 && (
                                            <Chip
                                                icon={
                                                    <FireIcon
                                                        sx={{
                                                            color:
                                                                child.currentStreak >=
                                                                3
                                                                    ? "#fff !important"
                                                                    : "inherit",
                                                        }}
                                                    />
                                                }
                                                label={`${child.currentStreak || 0} pv`}
                                                size="small"
                                                sx={{
                                                    bgcolor:
                                                        child.currentStreak >= 3
                                                            ? statusColors.streak
                                                            : colors.chipStreakBg,
                                                    color:
                                                        child.currentStreak >= 3
                                                            ? "#fff"
                                                            : colors.textSecondary,
                                                    fontWeight:
                                                        colors.effects
                                                            ?.fontWeightPixel ||
                                                        700,
                                                    fontSize: "0.7rem",
                                                    height: 26,
                                                    borderRadius:
                                                        colors.components?.chip
                                                            ?.borderRadius ??
                                                        50,
                                                    border:
                                                        colors.components?.chip
                                                            ?.border || "none",
                                                    fontFamily:
                                                        colors.typography
                                                            ?.mono || "inherit",
                                                    "& .MuiChip-icon": {
                                                        ml: 0.5,
                                                    },
                                                }}
                                            />
                                        )}
                                        {child.freezeAvailable > 0 && (
                                            <Chip
                                                icon={
                                                    <ShieldIcon
                                                        sx={{
                                                            color: "#fff !important",
                                                            fontSize: 16,
                                                        }}
                                                    />
                                                }
                                                label="SUOJA"
                                                onClick={() => {
                                                    setShieldChildId(child.id);
                                                    setShieldInfoOpen(true);
                                                }}
                                                size="small"
                                                sx={{
                                                    bgcolor:
                                                        statusColors.shield,
                                                    color: "#fff",
                                                    fontWeight: 700,
                                                    fontSize: "0.7rem",
                                                    height: 26,
                                                    borderRadius:
                                                        colors.components?.chip
                                                            ?.borderRadius ??
                                                        50,
                                                    "&:hover": {
                                                        bgcolor: "#0097a7",
                                                    },
                                                    cursor: "pointer",
                                                    "& .MuiChip-icon": {
                                                        ml: 0.5,
                                                    },
                                                }}
                                            />
                                        )}
                                        {child.freezeAvailable === 0 &&
                                            child.currentStreak > 0 && (
                                                <Chip
                                                    icon={
                                                        <ShieldIcon
                                                            sx={{
                                                                color: "#999 !important",
                                                                fontSize: 16,
                                                            }}
                                                        />
                                                    }
                                                    label="SUOJA"
                                                    onClick={() => {
                                                        setShieldChildId(
                                                            child.id,
                                                        );
                                                        setShieldInfoOpen(true);
                                                    }}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: "#e0e0e0",
                                                        color: "#999",
                                                        fontWeight: 700,
                                                        fontSize: "0.7rem",
                                                        height: 26,
                                                        borderRadius:
                                                            colors.components
                                                                ?.chip
                                                                ?.borderRadius ??
                                                            50,
                                                        "&:hover": {
                                                            bgcolor: "#d0d0d0",
                                                        },
                                                        cursor: "pointer",
                                                        "& .MuiChip-icon": {
                                                            ml: 0.5,
                                                        },
                                                    }}
                                                />
                                            )}
                                    </Box>

                                    {/* XP bar - inside profile group */}
                                    <LinearProgress
                                        variant="determinate"
                                        value={xpProgress}
                                        sx={{
                                            height: 10,
                                            borderRadius: colors.palette?.retro
                                                ? 0
                                                : 50,
                                            bgcolor: `${colors.accent}22`,
                                            border: colors.palette?.retro
                                                ? colors.effects.borderThin
                                                : "none",
                                            mt: 1,
                                            mb: 0.5,
                                            "& .MuiLinearProgress-bar": {
                                                bgcolor: colors.accent,
                                                borderRadius: colors.palette
                                                    ?.retro
                                                    ? 0
                                                    : 50,
                                            },
                                        }}
                                    />

                                    {/* Coins pill LEFT, XP text RIGHT - same line */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                        }}
                                    >
                                        {/* Coins pill */}
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                                bgcolor: colors.accent,
                                                borderRadius: 50,
                                                px: 1.25,
                                                height: 26,
                                            }}
                                        >
                                            <Typography
                                                sx={{ fontSize: "0.85rem" }}
                                            >
                                                🪙
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                fontWeight={900}
                                                sx={{
                                                    color: "#fff",
                                                    lineHeight: 1,
                                                    fontSize: "0.8rem",
                                                    fontFamily:
                                                        colors.typography
                                                            ?.body || "inherit",
                                                }}
                                            >
                                                {child.coins || 0}
                                            </Typography>
                                        </Box>

                                        {/* XP text */}
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontSize: "0.75rem",
                                                color: colors.palette?.retro
                                                    ? colors.palette.retro
                                                          .textWhite
                                                    : colors.textSecondary,
                                                fontFamily:
                                                    colors.typography?.mono ||
                                                    "inherit",
                                                fontWeight: 600,
                                                textShadow: colors.palette
                                                    ?.retro
                                                    ? colors.effects
                                                          .textShadowHard
                                                    : "none",
                                            }}
                                        >
                                            {totalXp}/{nextLevelXp} XP
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>

                        {/* Scrollable area: Achievements, Boosters, Weekly goal, Tasks, Bonus */}
                        <Box
                            data-task-scroll
                            sx={{
                                px: 2,
                                pb: isSingleChildMobile
                                    ? `calc(56px + env(safe-area-inset-bottom, 0px) + 16px)`
                                    : isMobile
                                      ? `calc(56px + env(safe-area-inset-bottom, 0px) + 16px)`
                                      : 2,
                                flex: 1,
                                overflow: isSingleChildMobile
                                    ? "auto"
                                    : isMobile
                                      ? "visible"
                                      : "auto",
                                display: "flex",
                                flexDirection: "column",
                                position: "relative",
                                zIndex: 1,
                                minHeight: 0,
                            }}
                        >
                            {/* Boosters + money bag + pending conversion */}
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 1,
                                    flexWrap: "wrap",
                                    alignItems: "center",
                                    mb: 1,
                                }}
                            >
                                {/* Active boosters */}
                                {child.activeBoosters?.length > 0 &&
                                    child.activeBoosters.map((b) => {
                                        const daysLeft = b.expires_at
                                            ? Math.max(
                                                  0,
                                                  Math.ceil(
                                                      (new Date(b.expires_at) -
                                                          Date.now()) /
                                                          86400000,
                                                  ),
                                              )
                                            : null;
                                        return (
                                            <Chip
                                                key={b.id}
                                                label={`🔥 ${b.multiplier_value}x pisteet${daysLeft !== null ? ` (${daysLeft}pv)` : ""}`}
                                                size="small"
                                                sx={{
                                                    bgcolor: statusColors.bonus,
                                                    color: "#fff",
                                                    fontWeight: 700,
                                                    fontSize: "0.7rem",
                                                    height: 24,
                                                }}
                                            />
                                        );
                                    })}

                                {/* Pending conversion */}
                                {hasPendingConversion && (
                                    <Box
                                        sx={{
                                            bgcolor: `${colors.accent}15`,
                                            borderRadius: "10px",
                                            px: 1.5,
                                            py: 0.5,
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            fontWeight={600}
                                            color={colors.accent}
                                            sx={{ fontSize: "0.7rem" }}
                                        >
                                            ⏳ Odottaa viikkorahaa
                                        </Typography>
                                    </Box>
                                )}
                            </Box>

                            {/* Weekly goal progress — redesigned */}
                            <Box
                                sx={{
                                    bgcolor:
                                        hasBgImage && colors.effectiveDark
                                            ? "rgba(20, 20, 20, 0.85)"
                                            : colors.weeklyGoalBg,
                                    borderRadius:
                                        colors.components?.weeklyGoalCard
                                            ?.borderRadius ??
                                        colors.weeklyGoalBorderRadius,
                                    p: 1.5,
                                    mb: 1,
                                    border:
                                        colors.weeklyGoalBorder ||
                                        colors.components?.card?.border ||
                                        "none",
                                    boxShadow:
                                        colors.components?.card?.boxShadow ||
                                        "none",
                                    position: "relative",
                                    zIndex: 1,
                                    ...(colors.isSuper &&
                                        !colors.palette?.retro && {
                                            "&::before": {
                                                content: '""',
                                                position: "absolute",
                                                inset: 0,
                                                borderRadius:
                                                    colors.components
                                                        ?.weeklyGoalCard
                                                        ?.borderRadius ??
                                                    "12px",
                                                padding: "1px",
                                                background: `linear-gradient(90deg, ${colors.accent}, ${colors.light}, ${colors.accent})`,
                                                backgroundSize: "300% 100%",
                                                animation:
                                                    "glowBorder 3s linear infinite",
                                                WebkitMask:
                                                    "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                                                WebkitMaskComposite: "xor",
                                                maskComposite: "exclude",
                                            },
                                            "@keyframes glowBorder": {
                                                "0%": {
                                                    backgroundPosition:
                                                        "0% 50%",
                                                },
                                                "100%": {
                                                    backgroundPosition:
                                                        "300% 50%",
                                                },
                                            },
                                        }),
                                    "@keyframes waveFlow": {
                                        "0%": { backgroundPosition: "0% 50%" },
                                        "100%": {
                                            backgroundPosition: "200% 50%",
                                        },
                                    },
                                }}
                            >
                                {/* Viikkotavoite header row - just label + bonus badge when achieved */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mb: 0.5,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        fontWeight={
                                            colors.effects?.fontWeightPixel ||
                                            900
                                        }
                                        sx={{
                                            textTransform: "uppercase",
                                            letterSpacing: 1,
                                            fontSize: colors.palette?.retro
                                                ? "0.65rem"
                                                : "0.7rem",
                                            color: colors.palette?.retro
                                                ? colors.palette.retro.textWhite
                                                : colors.textSecondary,
                                            fontFamily:
                                                colors.typography?.mono ||
                                                "inherit",
                                            textShadow: colors.palette?.retro
                                                ? colors.effects.textShadowHard
                                                : "none",
                                        }}
                                    >
                                        Viikkotavoite
                                    </Typography>
                                    {goalAchieved && (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                                bgcolor: colors.palette?.retro
                                                    ? colors.palette.retro
                                                          .bonusYellow
                                                    : statusColors.bonus,
                                                borderRadius: colors.palette
                                                    ?.retro
                                                    ? 0
                                                    : "12px",
                                                px: 1,
                                                py: 0.25,
                                                border: colors.palette?.retro
                                                    ? colors.effects.borderThin
                                                    : "none",
                                            }}
                                        >
                                            <Typography
                                                sx={{ fontSize: "0.9rem" }}
                                            >
                                                🪙
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                fontWeight={
                                                    colors.effects
                                                        ?.fontWeightPixel || 900
                                                }
                                                sx={{
                                                    color: colors.palette?.retro
                                                        ? colors.palette.retro
                                                              .textBlack
                                                        : "#fff",
                                                    fontSize: "0.9rem",
                                                    fontFamily:
                                                        colors.typography
                                                            ?.body || "inherit",
                                                }}
                                            >
                                                +{weeklyPoints - weeklyGoal}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>

                                {/* 70/30 split progress bar */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        height: 12,
                                        borderRadius:
                                            colors.components?.progressBar
                                                ?.borderRadius ?? 50,
                                        overflow: "hidden",
                                        border: colors.palette?.retro
                                            ? colors.effects.borderThin
                                            : colors.components?.progressBar
                                                  ?.border || "none",
                                    }}
                                >
                                    {/* Points zone (70% of bar width) */}
                                    <Box
                                        sx={{
                                            width: "70%",
                                            bgcolor: colors.progressTrackBg,
                                            position: "relative",
                                            borderRadius:
                                                colors.components?.progressBar
                                                    ?.borderRadius != null
                                                    ? `${colors.components.progressBar.borderRadius}px 0 0 ${colors.components.progressBar.borderRadius}px`
                                                    : "50px 0 0 50px",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                left: 0,
                                                top: 0,
                                                bottom: 0,
                                                width: `${Math.min(100, goalPct)}%`,
                                                bgcolor: colors.accent,
                                                borderRadius:
                                                    colors.components
                                                        ?.progressBar
                                                        ?.borderRadius != null
                                                        ? `${colors.components.progressBar.borderRadius}px 0 0 ${colors.components.progressBar.borderRadius}px`
                                                        : "50px 0 0 50px",
                                                transition: "width 0.5s ease",
                                                overflow: "visible",
                                            }}
                                        >
                                            {/* Liquid animation at the edge */}
                                            {!goalAchieved &&
                                                goalPct > 0 &&
                                                goalPct < 100 && (
                                                    <Box
                                                        sx={{
                                                            position:
                                                                "absolute",
                                                            right: -10,
                                                            top: -4,
                                                            width: 20,
                                                            height: 20,
                                                            bgcolor:
                                                                colors.accent,
                                                            borderRadius: "40%",
                                                            zIndex: 1,
                                                            "@keyframes liquidRotate":
                                                                {
                                                                    "0%": {
                                                                        transform:
                                                                            "rotate(0deg)",
                                                                    },
                                                                    "100%": {
                                                                        transform:
                                                                            "rotate(360deg)",
                                                                    },
                                                                },
                                                            animation:
                                                                "liquidRotate 5s infinite cubic-bezier(0.5, 0.5, 0.5, 0.5)",
                                                        }}
                                                    />
                                                )}
                                        </Box>
                                    </Box>
                                    {/* Bonus zone (30% of bar width) */}
                                    <Box
                                        sx={{
                                            width: "30%",
                                            bgcolor: colors.progressBonusZoneBg,
                                            position: "relative",
                                            borderRadius:
                                                colors.components?.progressBar
                                                    ?.borderRadius != null
                                                    ? `0 ${colors.components.progressBar.borderRadius}px ${colors.components.progressBar.borderRadius}px 0`
                                                    : "0 50px 50px 0",
                                            borderLeft: goalAchieved
                                                ? "none"
                                                : "2px solid #ffc107",
                                        }}
                                    >
                                        {goalAchieved && (
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    left: 0,
                                                    top: 0,
                                                    bottom: 0,
                                                    width: "100%",
                                                    bgcolor: statusColors.bonus,
                                                    borderRadius:
                                                        colors.components
                                                            ?.progressBar
                                                            ?.borderRadius !=
                                                        null
                                                            ? `0 ${colors.components.progressBar.borderRadius}px ${colors.components.progressBar.borderRadius}px 0`
                                                            : "0 50px 50px 0",
                                                    transition:
                                                        "width 0.5s ease",
                                                }}
                                            />
                                        )}
                                    </Box>
                                </Box>

                                {/* Bottom row: Pisteet left, Bonus-alue chip right */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mt: 0.5,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontSize: "0.7rem",
                                            color: colors.palette?.retro
                                                ? colors.palette.retro.textWhite
                                                : colors.textPrimary,
                                            fontWeight: 600,
                                            fontFamily:
                                                colors.typography?.mono ||
                                                "inherit",
                                            textShadow: colors.palette?.retro
                                                ? colors.effects.textShadowHard
                                                : "none",
                                        }}
                                    >
                                        Pisteet{" "}
                                        <Box
                                            component="span"
                                            sx={{
                                                color: goalAchieved
                                                    ? statusColors.bonus
                                                    : colors.accent,
                                                fontWeight: 700,
                                            }}
                                        >
                                            {weeklyPoints}/{weeklyGoal}
                                        </Box>{" "}
                                        ({goalPct}%)
                                    </Typography>
                                    <Chip
                                        label="🪙 Bonus-alue"
                                        size="small"
                                        sx={{
                                            bgcolor: goalAchieved
                                                ? colors.palette?.retro
                                                    ? colors.palette.retro
                                                          .bonusYellow
                                                    : statusColors.bonus
                                                : "transparent",
                                            color: goalAchieved
                                                ? colors.palette?.retro
                                                    ? colors.palette.retro
                                                          .textBlack
                                                    : "#fff"
                                                : statusColors.bonus,
                                            fontWeight: 700,
                                            fontSize: "0.65rem",
                                            height: 17,
                                            border: "none",
                                            borderRadius: colors.palette?.retro
                                                ? 0
                                                : 50,
                                        }}
                                    />
                                </Box>
                            </Box>

                            {/* Task counter — aligned left, above tasks */}
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    mb: 0.5,
                                }}
                            >
                                <StarIcon
                                    sx={{
                                        color: bgTextColor || colors.accent,
                                        fontSize: 18,
                                        filter: hasBgImage
                                            ? "drop-shadow(0 1px 2px rgba(0,0,0,0.5))"
                                            : "none",
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    fontWeight={
                                        colors.effects?.fontWeightPixel || 600
                                    }
                                    sx={{
                                        color: colors.palette?.retro
                                            ? colors.palette.retro.textYellow
                                            : bgTextColor || colors.accent,
                                        fontFamily:
                                            colors.typography?.body ||
                                            "inherit",
                                        textShadow: colors.palette?.retro
                                            ? colors.effects.textShadowHard
                                            : bgTextShadow,
                                    }}
                                >
                                    {remaining > 0
                                        ? `${remaining} jäljellä!`
                                        : "🎉 Kaikki tehty!"}
                                </Typography>
                                {(completed > 0 ||
                                    dismissedTasks[child.id]?.size > 0) && (
                                    <Chip
                                        label={
                                            hideCompleted
                                                ? "Näytä valmiit"
                                                : "Piilota valmiit"
                                        }
                                        size="small"
                                        variant="outlined"
                                        onClick={() =>
                                            setHideCompleted((prev) => !prev)
                                        }
                                        sx={{
                                            ml: "auto",
                                            fontWeight: 600,
                                            fontSize: "0.7rem",
                                            height: 28,
                                            px: 0.5,
                                            borderColor:
                                                bgTextColor || colors.accent,
                                            color: bgTextColor || colors.accent,
                                            textShadow: bgTextShadow,
                                        }}
                                    />
                                )}
                            </Box>

                            {total === 0 && scheduledTasks.length === 0 ? (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ textAlign: "center", py: 3 }}
                                >
                                    Ei tehtäviä tänään
                                </Typography>
                            ) : (
                                <>
                                    {/* Normal tasks zone */}
                                    {normalTasks
                                        .filter((task) => {
                                            const isDismissed = dismissedTasks[
                                                child.id
                                            ]?.has(task.id);
                                            if (hideCompleted) {
                                                return (
                                                    !task.completed &&
                                                    !task.completedByOther &&
                                                    !isDismissed
                                                );
                                            }
                                            return true;
                                        })
                                        .map((task, taskIdx) => {
                                            const isSharedTask =
                                                !task.assigned_to ||
                                                task.assigned_to === "";
                                            const isClickable =
                                                !task.completed &&
                                                !task.completedByOther;

                                            // Nameplate background for themed task buttons
                                            // Prefer child's task_animation over theme's nameplate
                                            const npSrc = child.task_animation
                                                ? `/uploads/profile-effects/${child.task_animation}/bg.json`
                                                : colors.nameplate?.src;
                                            const npData = npSrc
                                                ? nameplateData[npSrc]
                                                : null;
                                            // All incomplete tasks get animated
                                            const isIncomplete =
                                                !task.completed &&
                                                !task.completedByOther;
                                            const isDismissed = dismissedTasks[
                                                child.id
                                            ]?.has(task.id);
                                            const nameplateImg = npData?.assets
                                                ? isIncomplete
                                                    ? npData.assets.animated
                                                    : npData.assets.static
                                                : null;

                                            return (
                                                <motion.div
                                                    key={task.id}
                                                    initial={{
                                                        opacity: 0,
                                                        y: 6,
                                                    }}
                                                    animate={{
                                                        opacity: 1,
                                                        y: 0,
                                                    }}
                                                    transition={{
                                                        duration: 0.2,
                                                    }}
                                                    style={{ marginBottom: 8 }}
                                                >
                                                    <RainbowBorder
                                                        active={
                                                            !!colors.animations
                                                                ?.taskRainbowBorder &&
                                                            !task.completed &&
                                                            !task.completedByOther
                                                        }
                                                        borderRadius={parseInt(
                                                            colors.components
                                                                ?.card
                                                                ?.borderRadius ||
                                                                colors.shapes
                                                                    ?.taskRow ||
                                                                "20",
                                                            10,
                                                        )}
                                                        borderWidth={2}
                                                        speed={4}
                                                    >
                                                        <Box
                                                            ref={(el) => {
                                                                if (
                                                                    isSharedTask
                                                                )
                                                                    taskRefs.current[
                                                                        `${child.id}-${task.id}`
                                                                    ] = el;
                                                            }}
                                                            onClick={() => {
                                                                if (
                                                                    interactionLocked
                                                                )
                                                                    return;
                                                                if (
                                                                    isDismissed
                                                                ) {
                                                                    restoreTask(
                                                                        child.id,
                                                                        task.id,
                                                                    );
                                                                    return;
                                                                }
                                                                if (
                                                                    task.completed ||
                                                                    task.completedByOther
                                                                )
                                                                    return;
                                                                // If confirmation is already showing for this task, dismiss it
                                                                if (
                                                                    pendingConfirm?.taskId ===
                                                                        task.id &&
                                                                    pendingConfirm?.childId ===
                                                                        child.id
                                                                ) {
                                                                    setPendingConfirm(
                                                                        null,
                                                                    );
                                                                    return;
                                                                }
                                                                // If confirmation showing for different task, dismiss it
                                                                if (
                                                                    pendingConfirm
                                                                ) {
                                                                    setPendingConfirm(
                                                                        null,
                                                                    );
                                                                    return;
                                                                }
                                                                setPendingConfirm(
                                                                    {
                                                                        taskId: task.id,
                                                                        childId:
                                                                            child.id,
                                                                        task,
                                                                        accentColor:
                                                                            colors.accent,
                                                                        goalAchieved,
                                                                        colors,
                                                                    },
                                                                );
                                                            }}
                                                            sx={{
                                                                display: "flex",
                                                                alignItems:
                                                                    "center",
                                                                gap: 1.5,
                                                                position:
                                                                    "relative",
                                                                overflow:
                                                                    "hidden",
                                                                bgcolor:
                                                                    task.completed
                                                                        ? colors.taskCompleteBackground
                                                                            ? "transparent"
                                                                            : colors.taskCompleteBg
                                                                        : nameplateImg
                                                                          ? colors.dark ||
                                                                            colors.accent
                                                                          : effectiveCardBg,
                                                                background:
                                                                    task.completed &&
                                                                    colors.taskCompleteBackground
                                                                        ? colors.taskCompleteBackground
                                                                        : undefined,
                                                                backgroundImage:
                                                                    nameplateImg &&
                                                                    !task.completed
                                                                        ? `url(${nameplateImg})`
                                                                        : undefined,
                                                                backgroundSize:
                                                                    "cover",
                                                                backgroundPosition:
                                                                    "center",
                                                                borderRadius:
                                                                    colors
                                                                        .components
                                                                        ?.card
                                                                        ?.borderRadius ??
                                                                    colors
                                                                        .shapes
                                                                        .taskRow,
                                                                px: 1.5,
                                                                py: 1.25,
                                                                cursor: isClickable
                                                                    ? "pointer"
                                                                    : "default",
                                                                opacity:
                                                                    isDismissed
                                                                        ? 0.4
                                                                        : 1,
                                                                boxShadow:
                                                                    task.completed
                                                                        ? colors.taskBoxShadowCompleted
                                                                        : task.overdue
                                                                          ? colors.taskBoxShadowOverdue
                                                                          : colors.taskBoxShadow,
                                                                border: task.completedByOther
                                                                    ? colors.taskBorderCompletedByOther
                                                                    : task.overdue &&
                                                                        !task.completed
                                                                      ? colors.taskBorderOverdue
                                                                      : colors.taskBorder,
                                                                transition:
                                                                    "transform 0.1s ease, box-shadow 0.1s ease",
                                                                "&:hover":
                                                                    isClickable
                                                                        ? {
                                                                              transform:
                                                                                  "translateY(-2px)",
                                                                              boxShadow:
                                                                                  colors.taskBoxShadowHover,
                                                                          }
                                                                        : {},
                                                                "&:active":
                                                                    isClickable
                                                                        ? {
                                                                              transform:
                                                                                  "translateY(2px)",
                                                                              boxShadow:
                                                                                  colors.taskBoxShadowActive,
                                                                          }
                                                                        : {},
                                                            }}
                                                        >
                                                            {/* Icon box */}
                                                            <Box
                                                                sx={{
                                                                    width: 44,
                                                                    height: 44,
                                                                    borderRadius:
                                                                        colors
                                                                            .components
                                                                            ?.avatar
                                                                            ?.borderRadius ??
                                                                        "14px",
                                                                    bgcolor:
                                                                        task.completed
                                                                            ? "rgba(0,0,0,0.7)"
                                                                            : task.overdue
                                                                              ? contrast.withAlpha(
                                                                                    statusColors.overdue,
                                                                                    effectiveDark
                                                                                        ? 0.35
                                                                                        : 0.15,
                                                                                )
                                                                              : "rgba(0,0,0,0.7)",
                                                                    display:
                                                                        "flex",
                                                                    alignItems:
                                                                        "center",
                                                                    justifyContent:
                                                                        "center",
                                                                    flexShrink: 0,
                                                                    border:
                                                                        colors
                                                                            .components
                                                                            ?.avatar
                                                                            ?.border ||
                                                                        "none",
                                                                }}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        fontSize:
                                                                            "1.8rem",
                                                                    }}
                                                                >
                                                                    {task.icon ||
                                                                        "📋"}
                                                                </Typography>
                                                            </Box>

                                                            {/* Title + points */}
                                                            <Box
                                                                sx={{
                                                                    flex: 1,
                                                                    minWidth: 0,
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        display:
                                                                            "flex",
                                                                        alignItems:
                                                                            "center",
                                                                        gap: 0.5,
                                                                        flexWrap:
                                                                            "wrap",
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="body2"
                                                                        fontWeight={
                                                                            colors.taskFontWeight
                                                                        }
                                                                        sx={{
                                                                            color: task.completed
                                                                                ? colors.taskCompleteText
                                                                                : task.completedByOther &&
                                                                                    nameplateImg
                                                                                  ? colors.textPrimary
                                                                                  : nameplateImg
                                                                                    ? colors.taskPendingText ||
                                                                                      "#fff"
                                                                                    : colors.textPrimary,
                                                                            textDecoration:
                                                                                task.completedByOther
                                                                                    ? "line-through"
                                                                                    : "none",
                                                                            whiteSpace:
                                                                                "nowrap",
                                                                            overflow:
                                                                                "hidden",
                                                                            textOverflow:
                                                                                "ellipsis",
                                                                            fontFamily:
                                                                                colors
                                                                                    .typography
                                                                                    ?.body ||
                                                                                "inherit",
                                                                        }}
                                                                    >
                                                                        {
                                                                            task.title
                                                                        }
                                                                    </Typography>
                                                                    {task.overdue &&
                                                                        !task.completed && (
                                                                            <Typography
                                                                                variant="caption"
                                                                                sx={{
                                                                                    color: "#fff",
                                                                                    fontWeight: 700,
                                                                                    fontSize:
                                                                                        "0.65rem",
                                                                                    bgcolor:
                                                                                        statusColors.overdue,
                                                                                    px: 0.5,
                                                                                    borderRadius: 1,
                                                                                }}
                                                                            >
                                                                                myöhässä
                                                                            </Typography>
                                                                        )}
                                                                    {task.pendingApproval && (
                                                                        <Box
                                                                            sx={{
                                                                                display:
                                                                                    "flex",
                                                                                alignItems:
                                                                                    "center",
                                                                                gap: 0.25,
                                                                                bgcolor:
                                                                                    "#fff3e0",
                                                                                px: 0.5,
                                                                                borderRadius: 1,
                                                                            }}
                                                                        >
                                                                            <ApprovalIcon
                                                                                sx={{
                                                                                    fontSize:
                                                                                        "0.75rem",
                                                                                    color: "#ff9800",
                                                                                }}
                                                                            />
                                                                            <Typography
                                                                                variant="caption"
                                                                                sx={{
                                                                                    color: "#ff9800",
                                                                                    fontWeight: 700,
                                                                                    fontSize:
                                                                                        "0.65rem",
                                                                                }}
                                                                            >
                                                                                odottaa
                                                                                hyväksyntää
                                                                            </Typography>
                                                                        </Box>
                                                                    )}
                                                                    {!task.completed &&
                                                                        task.requires_approval ===
                                                                            1 &&
                                                                        !task.pendingApproval && (
                                                                            <ApprovalIcon
                                                                                sx={{
                                                                                    fontSize:
                                                                                        "0.85rem",
                                                                                    color: "#ff9800",
                                                                                }}
                                                                                titleAccess="Vaatii hyväksynnän"
                                                                            />
                                                                        )}
                                                                    {!task.completed &&
                                                                        task.is_locked ===
                                                                            1 && (
                                                                            <LockIcon
                                                                                sx={{
                                                                                    fontSize:
                                                                                        "0.85rem",
                                                                                    color: colors.effectiveDark
                                                                                        ? "rgba(255,255,255,0.8)"
                                                                                        : "rgba(0,0,0,0.6)",
                                                                                }}
                                                                                titleAccess="Lukittu tehtävä"
                                                                            />
                                                                        )}
                                                                    {task.time_start &&
                                                                        task.time_end &&
                                                                        !task.completed &&
                                                                        !task.completedByOther && (
                                                                            <LiveTimer
                                                                                startTime={
                                                                                    task.time_start
                                                                                }
                                                                                endTime={
                                                                                    task.time_end
                                                                                }
                                                                                variant="task"
                                                                                size="small"
                                                                                onExpired={
                                                                                    loadData
                                                                                }
                                                                            />
                                                                        )}
                                                                    {task.time_start &&
                                                                        task.completed && (
                                                                            <Typography
                                                                                variant="caption"
                                                                                sx={{
                                                                                    color:
                                                                                        colors
                                                                                            .effects
                                                                                            ?.completedTextColor ||
                                                                                        "rgba(255,255,255,0.7)",
                                                                                    fontWeight: 600,
                                                                                    fontSize:
                                                                                        "0.65rem",
                                                                                }}
                                                                            >
                                                                                {
                                                                                    task.time_start
                                                                                }

                                                                                –
                                                                                {
                                                                                    task.time_end
                                                                                }
                                                                            </Typography>
                                                                        )}
                                                                </Box>
                                                                {task.description ? (
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            color: task.completed
                                                                                ? colors.taskCompleteTextSecondary ||
                                                                                  "rgba(255,255,255,0.75)"
                                                                                : nameplateImg
                                                                                  ? "rgba(255,255,255,0.75)"
                                                                                  : colors.textSecondary,
                                                                            display:
                                                                                "block",
                                                                            whiteSpace:
                                                                                "nowrap",
                                                                            overflow:
                                                                                "hidden",
                                                                            textOverflow:
                                                                                "ellipsis",
                                                                        }}
                                                                    >
                                                                        {
                                                                            task.description
                                                                        }
                                                                    </Typography>
                                                                ) : null}
                                                                {task.cooldown_days >
                                                                    0 &&
                                                                    !task.completed &&
                                                                    !task.completedByOther && (
                                                                        <Typography
                                                                            variant="caption"
                                                                            sx={{
                                                                                color: nameplateImg
                                                                                    ? "rgba(255,255,255,0.7)"
                                                                                    : colors.textMuted,
                                                                                fontSize:
                                                                                    "0.65rem",
                                                                                display:
                                                                                    "flex",
                                                                                alignItems:
                                                                                    "center",
                                                                                gap: 0.5,
                                                                            }}
                                                                        >
                                                                            ⏸️{" "}
                                                                            {
                                                                                task.cooldown_days
                                                                            }{" "}
                                                                            pv
                                                                            jäädytys
                                                                        </Typography>
                                                                    )}
                                                                {task.completedByOther && (
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            color: nameplateImg
                                                                                ? "rgba(255,255,255,0.7)"
                                                                                : colors.textMuted,
                                                                            fontSize:
                                                                                "0.65rem",
                                                                            fontWeight: 600,
                                                                        }}
                                                                    >
                                                                        ✓{" "}
                                                                        {task.completedByName ||
                                                                            "Toinen"}{" "}
                                                                        teki
                                                                    </Typography>
                                                                )}
                                                            </Box>

                                                            {/* Points display or restore button */}
                                                            {isDismissed ? (
                                                                <Box
                                                                    sx={{
                                                                        display:
                                                                            "flex",
                                                                        alignItems:
                                                                            "center",
                                                                        justifyContent:
                                                                            "center",
                                                                        px: 1.5,
                                                                        py: 0.5,
                                                                        borderRadius:
                                                                            "14px",
                                                                        bgcolor:
                                                                            "rgba(76,175,80,0.15)",
                                                                        flexShrink: 0,
                                                                        minWidth: 70,
                                                                    }}
                                                                >
                                                                    <UndoIcon
                                                                        sx={{
                                                                            color: "#4caf50",
                                                                            fontSize: 22,
                                                                        }}
                                                                    />
                                                                </Box>
                                                            ) : task.completed &&
                                                              user?.type ===
                                                                  "parent" ? (
                                                                <Box
                                                                    onClick={(
                                                                        e,
                                                                    ) => {
                                                                        e.stopPropagation();
                                                                        handleUndoCompletion(
                                                                            task.id,
                                                                            child.id,
                                                                            task.title,
                                                                        );
                                                                    }}
                                                                    sx={{
                                                                        display:
                                                                            "flex",
                                                                        alignItems:
                                                                            "center",
                                                                        justifyContent:
                                                                            "center",
                                                                        px: 1.5,
                                                                        py: 0.5,
                                                                        borderRadius:
                                                                            "14px",
                                                                        bgcolor:
                                                                            "error.main",
                                                                        boxShadow: 2,
                                                                        color: "#fff",
                                                                        flexShrink: 0,
                                                                        minWidth: 70,
                                                                        cursor: "pointer",
                                                                    }}
                                                                >
                                                                    <RestoreIcon
                                                                        sx={{
                                                                            color: "#fff",
                                                                            fontSize: 22,
                                                                        }}
                                                                    />
                                                                </Box>
                                                            ) : (
                                                                <Box
                                                                    sx={{
                                                                        display:
                                                                            "flex",
                                                                        alignItems:
                                                                            "center",
                                                                        justifyContent:
                                                                            "center",
                                                                        px: 1.5,
                                                                        py: 0.5,
                                                                        borderRadius:
                                                                            "14px",
                                                                        bgcolor:
                                                                            task.completed
                                                                                ? colors
                                                                                      .palette
                                                                                      ?.taskCompleteCoinBg ||
                                                                                  colors.main ||
                                                                                  colors.accent
                                                                                : nameplateImg
                                                                                  ? colors
                                                                                        .palette
                                                                                        ?.taskPendingCoinBg ||
                                                                                    `${colors.accent}50`
                                                                                  : goalAchieved
                                                                                    ? "rgba(255,193,7,0.2)"
                                                                                    : `${colors.accent}18`,
                                                                        flexShrink: 0,
                                                                        minWidth: 70,
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="body1"
                                                                        fontWeight={
                                                                            900
                                                                        }
                                                                        sx={{
                                                                            color: task.completed
                                                                                ? "#fff"
                                                                                : nameplateImg
                                                                                  ? colors
                                                                                        .palette
                                                                                        ?.taskPendingCoinText ||
                                                                                    "#fff"
                                                                                  : goalAchieved
                                                                                    ? "#ff8f00"
                                                                                    : colors.accent,
                                                                            fontSize:
                                                                                "1.1rem",
                                                                        }}
                                                                    >
                                                                        {goalAchieved
                                                                            ? `🪙 +${task.reward_points}`
                                                                            : `+${task.reward_points}`}
                                                                    </Typography>
                                                                </Box>
                                                            )}

                                                            {/* Task confirmation overlay - radial gradient grow */}
                                                            <AnimatePresence mode="wait">
                                                                {pendingConfirm?.taskId ===
                                                                    task.id &&
                                                                    pendingConfirm?.childId ===
                                                                        child.id && (
                                                                        <motion.div
                                                                            key={`confirm-${task.id}`}
                                                                            initial={{
                                                                                opacity: 0,
                                                                            }}
                                                                            animate={{
                                                                                opacity: 1,
                                                                            }}
                                                                            exit={{
                                                                                opacity: 0,
                                                                            }}
                                                                            transition={{
                                                                                duration: 0.12,
                                                                            }}
                                                                            style={{
                                                                                position:
                                                                                    "absolute",
                                                                                inset: 0,
                                                                                display:
                                                                                    "flex",
                                                                                alignItems:
                                                                                    "center",
                                                                                justifyContent:
                                                                                    "center",
                                                                                zIndex: 10,
                                                                                transformOrigin:
                                                                                    "center center",
                                                                                background:
                                                                                    "radial-gradient(circle, rgba(0, 0, 0, 0.66) 0%, rgba(0, 0, 0, 0) 92%)",
                                                                                cursor: "pointer",
                                                                            }}
                                                                            onClick={(
                                                                                e,
                                                                            ) => {
                                                                                e.stopPropagation();
                                                                                setPendingConfirm(
                                                                                    null,
                                                                                );
                                                                            }}
                                                                        >
                                                                            {/* Dismiss button — left side */}
                                                                            <motion.div
                                                                                initial={{
                                                                                    scale: 0,
                                                                                    opacity: 0,
                                                                                }}
                                                                                animate={{
                                                                                    scale: 1,
                                                                                    opacity: 1,
                                                                                }}
                                                                                exit={{
                                                                                    scale: 0,
                                                                                    opacity: 0,
                                                                                }}
                                                                                transition={{
                                                                                    duration: 0.15,
                                                                                }}
                                                                                style={{
                                                                                    position:
                                                                                        "absolute",
                                                                                    left: 16,
                                                                                    top: 0,
                                                                                    bottom: 0,
                                                                                    display:
                                                                                        "flex",
                                                                                    alignItems:
                                                                                        "center",
                                                                                }}
                                                                            >
                                                                                <Box
                                                                                    onClick={(
                                                                                        e,
                                                                                    ) => {
                                                                                        e.stopPropagation();
                                                                                        dismissTask(
                                                                                            child.id,
                                                                                            task.id,
                                                                                        );
                                                                                    }}
                                                                                    sx={{
                                                                                        width: 44,
                                                                                        height: 44,
                                                                                        borderRadius:
                                                                                            "50%",
                                                                                        bgcolor:
                                                                                            "rgba(0,0,0,0.7)",
                                                                                        display:
                                                                                            "flex",
                                                                                        alignItems:
                                                                                            "center",
                                                                                        justifyContent:
                                                                                            "center",
                                                                                        cursor: "pointer",
                                                                                        transition:
                                                                                            "transform 0.15s, background-color 0.15s",
                                                                                        "&:hover":
                                                                                            {
                                                                                                bgcolor:
                                                                                                    "rgba(0,0,0,0.85)",
                                                                                                transform:
                                                                                                    "scale(1.1)",
                                                                                            },
                                                                                        "&:active":
                                                                                            {
                                                                                                transform:
                                                                                                    "scale(0.95)",
                                                                                            },
                                                                                    }}
                                                                                >
                                                                                    <DeleteIcon
                                                                                        sx={{
                                                                                            color: "#f44336",
                                                                                            fontSize: 24,
                                                                                        }}
                                                                                    />
                                                                                </Box>
                                                                            </motion.div>
                                                                            {/* Confirm button */}
                                                                            <motion.div
                                                                                initial={{
                                                                                    scale: 0,
                                                                                    opacity: 0,
                                                                                }}
                                                                                animate={{
                                                                                    scale: 1,
                                                                                    opacity: 1,
                                                                                }}
                                                                                exit={{
                                                                                    scale: 0,
                                                                                    opacity: 0,
                                                                                }}
                                                                                transition={{
                                                                                    duration: 0.15,
                                                                                }}
                                                                            >
                                                                                <Box
                                                                                    onClick={(
                                                                                        e,
                                                                                    ) => {
                                                                                        e.stopPropagation();
                                                                                        const p =
                                                                                            pendingConfirm;
                                                                                        setPendingConfirm(
                                                                                            null,
                                                                                        );
                                                                                        handleComplete(
                                                                                            p.taskId,
                                                                                            p.childId,
                                                                                            p.task,
                                                                                            p.accentColor,
                                                                                            p.goalAchieved,
                                                                                            p.colors,
                                                                                        );
                                                                                    }}
                                                                                    sx={{
                                                                                        width: 52,
                                                                                        height: 52,
                                                                                        borderRadius:
                                                                                            "50%",
                                                                                        bgcolor:
                                                                                            colors.accent,
                                                                                        boxShadow: `0 4px 12px ${contrast.withAlpha(colors.accent, 0.4)}`,
                                                                                        display:
                                                                                            "flex",
                                                                                        alignItems:
                                                                                            "center",
                                                                                        justifyContent:
                                                                                            "center",
                                                                                        cursor: "pointer",
                                                                                        transition:
                                                                                            "transform 0.15s, box-shadow 0.15s, background-color 0.15s",
                                                                                        "&:hover":
                                                                                            {
                                                                                                bgcolor:
                                                                                                    colors.dark ||
                                                                                                    contrast.darken(
                                                                                                        colors.accent,
                                                                                                        0.15,
                                                                                                    ),
                                                                                                transform:
                                                                                                    "scale(1.1)",
                                                                                                boxShadow: `0 6px 16px ${contrast.withAlpha(colors.accent, 0.5)}`,
                                                                                            },
                                                                                        "&:active":
                                                                                            {
                                                                                                transform:
                                                                                                    "scale(0.95)",
                                                                                            },
                                                                                    }}
                                                                                >
                                                                                    <CheckIcon
                                                                                        sx={{
                                                                                            color: "#fff",
                                                                                            fontSize: 32,
                                                                                        }}
                                                                                    />
                                                                                </Box>
                                                                            </motion.div>
                                                                        </motion.div>
                                                                    )}
                                                            </AnimatePresence>
                                                        </Box>
                                                    </RainbowBorder>
                                                </motion.div>
                                            );
                                        })}

                                    {/* Scheduled tasks zone — dotted divider */}
                                    {scheduledTasks.length > 0 && (
                                        <>
                                            <Box
                                                sx={{
                                                    borderBottom: "2px dashed",
                                                    borderColor:
                                                        colors.scheduledDivider,
                                                    my: 1,
                                                }}
                                            />
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: colors.palette?.retro
                                                        ? colors.palette.retro
                                                              .textWhite
                                                        : bgTextColor ||
                                                          colors.textSecondary,
                                                    fontWeight:
                                                        colors.effects
                                                            ?.fontWeightPixel ||
                                                        600,
                                                    textTransform: "uppercase",
                                                    letterSpacing: 1,
                                                    mb: 0.5,
                                                    display: "block",
                                                    fontSize: colors.palette
                                                        ?.retro
                                                        ? "0.65rem"
                                                        : "0.65rem",
                                                    fontFamily:
                                                        colors.typography
                                                            ?.mono || "inherit",
                                                    textShadow: colors.palette
                                                        ?.retro
                                                        ? colors.effects
                                                              .textShadowHard
                                                        : bgTextShadow,
                                                }}
                                            >
                                                ⏳ Ajastetut tehtävät
                                            </Typography>
                                            {scheduledTasks
                                                .filter((task) => {
                                                    const isDismissed =
                                                        dismissedTasks[
                                                            child.id
                                                        ]?.has(task.id);
                                                    if (hideCompleted)
                                                        return (
                                                            !task.completed &&
                                                            !task.completedByOther &&
                                                            !isDismissed
                                                        );
                                                    return true;
                                                })
                                                .map((task) => {
                                                    const isSharedTask =
                                                        !task.assigned_to ||
                                                        task.assigned_to === "";
                                                    const nowTime =
                                                        getCurrentDate();
                                                    const [sh] = task.time_start
                                                        .split(":")
                                                        .map(Number);
                                                    const startDate = new Date(
                                                        nowTime.getFullYear(),
                                                        nowTime.getMonth(),
                                                        nowTime.getDate(),
                                                        sh,
                                                        0,
                                                        0,
                                                    );
                                                    const isExpired =
                                                        nowTime > startDate &&
                                                        task.timeWindowStatus ===
                                                            "locked_past";

                                                    return (
                                                        <motion.div
                                                            key={task.id}
                                                            initial={{
                                                                opacity: 0,
                                                                y: 6,
                                                            }}
                                                            animate={{
                                                                opacity: 1,
                                                                y: 0,
                                                            }}
                                                            transition={{
                                                                duration: 0.2,
                                                            }}
                                                            style={{
                                                                marginBottom: 8,
                                                            }}
                                                        >
                                                            <Box
                                                                ref={(el) => {
                                                                    if (
                                                                        isSharedTask
                                                                    )
                                                                        taskRefs.current[
                                                                            `${child.id}-${task.id}`
                                                                        ] = el;
                                                                }}
                                                                sx={{
                                                                    display:
                                                                        "flex",
                                                                    alignItems:
                                                                        "center",
                                                                    gap: 1.5,
                                                                    bgcolor:
                                                                        colors
                                                                            .palette
                                                                            ?.retro
                                                                            ? colors
                                                                                  .palette
                                                                                  .retro
                                                                                  .scheduledBg
                                                                            : isExpired
                                                                              ? colors.scheduledExpiredBg
                                                                              : hasBgImage &&
                                                                                  colors.effectiveDark
                                                                                ? "rgba(20, 20, 20, 0.80)"
                                                                                : colors.scheduledTaskBg,
                                                                    borderRadius:
                                                                        colors
                                                                            .palette
                                                                            ?.retro
                                                                            ? 0
                                                                            : colors
                                                                                  .shapes
                                                                                  .taskRow,
                                                                    px: 1.5,
                                                                    py: 1.25,
                                                                    opacity:
                                                                        isExpired
                                                                            ? 0.5
                                                                            : 1,
                                                                    border: colors
                                                                        .palette
                                                                        ?.retro
                                                                        ? colors
                                                                              .effects
                                                                              .borderThin
                                                                        : isExpired
                                                                          ? `1px dashed ${colors.divider}`
                                                                          : `2px dashed ${colors.accent}`,
                                                                    boxShadow:
                                                                        colors
                                                                            .palette
                                                                            ?.retro
                                                                            ? colors
                                                                                  .effects
                                                                                  .pixelShadowSmall
                                                                            : isExpired
                                                                              ? "0px 3px 0px 0px rgba(0,0,0,0.1)"
                                                                              : `0px 4px 0px 0px ${colors.accent}44`,
                                                                }}
                                                            >
                                                                {/* Icon box — dimmed */}
                                                                <Box
                                                                    sx={{
                                                                        width: 44,
                                                                        height: 44,
                                                                        borderRadius:
                                                                            colors
                                                                                .components
                                                                                ?.avatar
                                                                                ?.borderRadius ??
                                                                            "14px",
                                                                        bgcolor:
                                                                            isExpired
                                                                                ? colors.scheduledExpiredBg
                                                                                : "rgba(0,0,0,0.7)",
                                                                        display:
                                                                            "flex",
                                                                        alignItems:
                                                                            "center",
                                                                        justifyContent:
                                                                            "center",
                                                                        flexShrink: 0,
                                                                        border:
                                                                            colors
                                                                                .components
                                                                                ?.avatar
                                                                                ?.border ||
                                                                            "none",
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        sx={{
                                                                            fontSize:
                                                                                "1.3rem",
                                                                            opacity: 0.6,
                                                                        }}
                                                                    >
                                                                        {task.icon ||
                                                                            "📋"}
                                                                    </Typography>
                                                                </Box>

                                                                {/* Title + time info */}
                                                                <Box
                                                                    sx={{
                                                                        flex: 1,
                                                                        minWidth: 0,
                                                                    }}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            display:
                                                                                "flex",
                                                                            alignItems:
                                                                                "center",
                                                                            gap: 0.5,
                                                                            flexWrap:
                                                                                "wrap",
                                                                        }}
                                                                    >
                                                                        <Typography
                                                                            variant="body2"
                                                                            fontWeight={
                                                                                colors
                                                                                    .effects
                                                                                    ?.fontWeightPixel ||
                                                                                700
                                                                            }
                                                                            sx={{
                                                                                color: isExpired
                                                                                    ? colors.textDisabled
                                                                                    : colors.textPrimary,
                                                                                whiteSpace:
                                                                                    "nowrap",
                                                                                overflow:
                                                                                    "hidden",
                                                                                textOverflow:
                                                                                    "ellipsis",
                                                                                fontFamily:
                                                                                    colors
                                                                                        .typography
                                                                                        ?.body ||
                                                                                    "inherit",
                                                                            }}
                                                                        >
                                                                            {
                                                                                task.title
                                                                            }
                                                                        </Typography>
                                                                        {isExpired ? (
                                                                            <Typography
                                                                                variant="caption"
                                                                                sx={{
                                                                                    color: colors.textMuted,
                                                                                    fontWeight: 600,
                                                                                    fontSize:
                                                                                        "0.65rem",
                                                                                }}
                                                                            >
                                                                                ⏰
                                                                                Aika
                                                                                umpeutui
                                                                            </Typography>
                                                                        ) : (
                                                                            <LiveTimer
                                                                                startTime={
                                                                                    task.time_start
                                                                                }
                                                                                endTime={
                                                                                    task.time_end
                                                                                }
                                                                                variant="task"
                                                                                size="small"
                                                                                onExpired={
                                                                                    loadData
                                                                                }
                                                                            />
                                                                        )}
                                                                    </Box>
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            color: colors.textMuted,
                                                                            fontSize:
                                                                                "0.65rem",
                                                                        }}
                                                                    >
                                                                        {
                                                                            task.time_start
                                                                        }
                                                                        –
                                                                        {
                                                                            task.time_end
                                                                        }
                                                                    </Typography>
                                                                </Box>

                                                                {/* Points display — dimmed */}
                                                                <Box
                                                                    sx={{
                                                                        display:
                                                                            "flex",
                                                                        alignItems:
                                                                            "center",
                                                                        justifyContent:
                                                                            "center",
                                                                        px: 1.5,
                                                                        py: 0.5,
                                                                        borderRadius:
                                                                            "14px",
                                                                        bgcolor:
                                                                            isExpired
                                                                                ? "rgba(0,0,0,0.05)"
                                                                                : `${colors.accent}11`,
                                                                        flexShrink: 0,
                                                                        minWidth: 70,
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="body1"
                                                                        fontWeight={
                                                                            900
                                                                        }
                                                                        sx={{
                                                                            color: isExpired
                                                                                ? colors.textDisabled
                                                                                : colors.accent,
                                                                            fontSize:
                                                                                "1.1rem",
                                                                            opacity:
                                                                                isExpired
                                                                                    ? 0.5
                                                                                    : 0.7,
                                                                        }}
                                                                    >
                                                                        +
                                                                        {
                                                                            task.reward_points
                                                                        }
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </motion.div>
                                                    );
                                                })}
                                        </>
                                    )}
                                </>
                            )}

                            {/* Bonus button */}
                            <Box
                                data-bonus-area
                                sx={{
                                    mt: "auto",
                                    pt: 2,
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                    position: isSingleChildMobile
                                        ? "relative"
                                        : "sticky",
                                    bottom: isSingleChildMobile
                                        ? undefined
                                        : isMobile
                                          ? `calc(56px + env(safe-area-inset-bottom, 0px))`
                                          : 0,
                                    flexShrink: 0,
                                    zIndex: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        position: "relative",
                                        display: "inline-flex",
                                        bgcolor: colors.bg,
                                        background:
                                            colors.background || colors.bg,
                                        borderRadius:
                                            colors.components?.button
                                                ?.borderRadius ?? 50,
                                    }}
                                >
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        startIcon={<BonusIcon />}
                                        onClick={() => {
                                            isBonusOpen
                                                ? setBonusOpen(null)
                                                : openBonus(child.id);
                                        }}
                                        sx={{
                                            borderRadius:
                                                colors.components?.button
                                                    ?.borderRadius ?? 50,
                                            borderColor: colors.palette?.retro
                                                ? colors.palette.retro.black
                                                : colors.accent,
                                            color: colors.palette?.retro
                                                ? colors.palette.retro.textBlack
                                                : colors.accent,
                                            bgcolor: colors.palette?.retro
                                                ? colors.palette.retro
                                                      .bonusYellow
                                                : "transparent",
                                            fontWeight:
                                                colors.effects
                                                    ?.fontWeightPixel || 700,
                                            fontSize: "0.8rem",
                                            fontFamily:
                                                colors.typography?.body ||
                                                "inherit",
                                            border:
                                                colors.components?.button
                                                    ?.border || undefined,
                                            boxShadow: colors.palette?.retro
                                                ? colors.effects.pixelShadow
                                                : colors.components?.button
                                                      ?.boxShadow ||
                                                  `0px 3px 0px 0px ${colors.accent}`,
                                            textTransform:
                                                colors.components?.button
                                                    ?.textTransform || "none",
                                            "&:hover": {
                                                bgcolor: colors.palette?.retro
                                                    ? colors.palette.retro
                                                          .bonusYellow
                                                    : `${colors.accent}11`,
                                                borderColor: colors.palette
                                                    ?.retro
                                                    ? colors.palette.retro.black
                                                    : colors.accent,
                                                transform: "translateY(-2px)",
                                                boxShadow: colors.palette?.retro
                                                    ? "6px 6px 0 #000000"
                                                    : colors.components?.button
                                                          ?.boxShadow ||
                                                      `0px 5px 0px 0px ${colors.accent}`,
                                            },
                                            "&:active": {
                                                transform: "translateY(2px)",
                                                boxShadow: colors.palette?.retro
                                                    ? "2px 2px 0 #000000"
                                                    : `0px 1px 0px 0px ${colors.accent}`,
                                            },
                                            transition:
                                                "transform 0.1s ease, box-shadow 0.1s ease",
                                        }}
                                    >
                                        Bonus-tehtävät
                                    </Button>
                                    {child.availableBonusCount > 0 && (
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                top: -6,
                                                right: -6,
                                                bgcolor: "#ff5252",
                                                color: "#fff",
                                                borderRadius: "50%",
                                                width: 22,
                                                height: 22,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: "0.7rem",
                                                fontWeight: 900,
                                                animation:
                                                    "pulse 1.5s infinite",
                                                boxShadow:
                                                    "0 2px 8px rgba(255,82,82,0.5)",
                                                zIndex: 10,
                                            }}
                                        >
                                            {child.availableBonusCount}
                                        </Box>
                                    )}
                                </Box>

                                {/* Bonus task picker — absolute overlay, doesn't push tasks */}
                                <AnimatePresence>
                                    {isBonusOpen && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{
                                                opacity: 1,
                                                y: 0,
                                            }}
                                            exit={{ opacity: 0, y: 20 }}
                                            transition={{ duration: 0.2 }}
                                            style={{
                                                position: "absolute",
                                                bottom: "100%",
                                                left: 0,
                                                right: 0,
                                                zIndex: 10,
                                                marginBottom: 0,
                                                maxHeight: "40vh",
                                                overflowY: "auto",
                                                pointerEvents: "auto",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    gap: 1,
                                                    bgcolor:
                                                        colors.bonusPanelBg,
                                                    borderRadius: 2,
                                                    borderTop:
                                                        colors.bonusPanelBorder,
                                                    p: 2,
                                                    boxShadow:
                                                        colors.bonusPanelBoxShadow,
                                                }}
                                            >
                                                {bonusTasks.length === 0 ? (
                                                    <Typography
                                                        variant="body2"
                                                        color={
                                                            colors.bonusPanelText
                                                        }
                                                        onClick={() => {
                                                            if (
                                                                interactionLocked
                                                            )
                                                                return;
                                                            setTaskRequestMessage(
                                                                "",
                                                            );
                                                            setTaskRequestDialog(
                                                                child.id,
                                                            );
                                                        }}
                                                        sx={{
                                                            textAlign: "center",
                                                            py: 1,
                                                            cursor: taskRequestSent[
                                                                child.id
                                                            ]
                                                                ? "default"
                                                                : "pointer",
                                                            opacity:
                                                                taskRequestSent[
                                                                    child.id
                                                                ]
                                                                    ? 0.6
                                                                    : 1,
                                                            "&:hover":
                                                                !taskRequestSent[
                                                                    child.id
                                                                ]
                                                                    ? {
                                                                          color: colors.accent,
                                                                      }
                                                                    : {},
                                                        }}
                                                    >
                                                        {taskRequestSent[
                                                            child.id
                                                        ]
                                                            ? "Pyyntö lähetetty ✓"
                                                            : "🙋 Pyydä lisää tehtäviä"}
                                                    </Typography>
                                                ) : (
                                                    bonusTasks.map((bt) => (
                                                        <Box
                                                            key={bt.id}
                                                            onClick={() =>
                                                                claimBonus(
                                                                    bt.id,
                                                                    child.id,
                                                                )
                                                            }
                                                            sx={{
                                                                display: "flex",
                                                                alignItems:
                                                                    "center",
                                                                gap: 1.5,
                                                                bgcolor:
                                                                    colors.bonusCardBg,
                                                                borderRadius:
                                                                    colors
                                                                        .components
                                                                        ?.card
                                                                        ?.borderRadius ??
                                                                    colors
                                                                        .shapes
                                                                        .taskRow,
                                                                px: 1.5,
                                                                py: 1.25,
                                                                cursor: "pointer",
                                                                boxShadow:
                                                                    colors
                                                                        .palette
                                                                        ?.retro
                                                                        ? colors
                                                                              .effects
                                                                              .taskShadow
                                                                        : colors
                                                                              .components
                                                                              ?.card
                                                                              ?.boxShadow ||
                                                                          (colors
                                                                              .effects
                                                                              ?.pixelShadow
                                                                              ? colors
                                                                                    .effects
                                                                                    .pixelShadow
                                                                              : `0px 3px 0px 0px ${colors.accent}`),
                                                                border: colors
                                                                    .palette
                                                                    ?.retro
                                                                    ? colors
                                                                          .components
                                                                          ?.card
                                                                          ?.border
                                                                    : colors
                                                                          .components
                                                                          ?.card
                                                                          ?.border ||
                                                                      `1.5px solid ${colors.accent}22`,
                                                                transition:
                                                                    "transform 0.1s ease, box-shadow 0.1s ease",
                                                                "&:hover": {
                                                                    transform:
                                                                        "translateY(-2px)",
                                                                    boxShadow:
                                                                        colors
                                                                            .palette
                                                                            ?.retro
                                                                            ? "6px 6px 0 #2563EB"
                                                                            : colors
                                                                                  .components
                                                                                  ?.card
                                                                                  ?.boxShadow ||
                                                                              colors
                                                                                  .effects
                                                                                  ?.pixelShadow ||
                                                                              `0px 5px 0px 0px ${colors.accent}`,
                                                                },
                                                                "&:active": {
                                                                    transform:
                                                                        "translateY(2px)",
                                                                    boxShadow:
                                                                        colors
                                                                            .palette
                                                                            ?.retro
                                                                            ? "2px 2px 0 #2563EB"
                                                                            : `0px 1px 0px 0px ${colors.accent}`,
                                                                },
                                                            }}
                                                        >
                                                            {/* Icon box */}
                                                            <Box
                                                                sx={{
                                                                    width: 44,
                                                                    height: 44,
                                                                    borderRadius:
                                                                        colors
                                                                            .components
                                                                            ?.avatar
                                                                            ?.borderRadius ??
                                                                        "14px",
                                                                    bgcolor:
                                                                        "rgba(0,0,0,0.7)",
                                                                    display:
                                                                        "flex",
                                                                    alignItems:
                                                                        "center",
                                                                    justifyContent:
                                                                        "center",
                                                                    flexShrink: 0,
                                                                    border:
                                                                        colors
                                                                            .components
                                                                            ?.avatar
                                                                            ?.border ||
                                                                        "none",
                                                                }}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        fontSize:
                                                                            "1.3rem",
                                                                    }}
                                                                >
                                                                    ⭐
                                                                </Typography>
                                                            </Box>
                                                            {/* Title + points */}
                                                            <Box
                                                                sx={{
                                                                    flex: 1,
                                                                    minWidth: 0,
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="body2"
                                                                    fontWeight={
                                                                        700
                                                                    }
                                                                    sx={{
                                                                        color: colors.textPrimary,
                                                                        fontFamily:
                                                                            colors
                                                                                .typography
                                                                                ?.body ||
                                                                            "inherit",
                                                                    }}
                                                                >
                                                                    {bt.title}
                                                                </Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    fontWeight={
                                                                        700
                                                                    }
                                                                    sx={{
                                                                        color: colors.accent,
                                                                        fontFamily:
                                                                            colors
                                                                                .typography
                                                                                ?.body ||
                                                                            "inherit",
                                                                    }}
                                                                >
                                                                    +
                                                                    {
                                                                        bt.reward_points
                                                                    }{" "}
                                                                    pistettä
                                                                </Typography>
                                                            </Box>
                                                            <BonusIcon
                                                                sx={{
                                                                    color: colors.accent,
                                                                    fontSize: 24,
                                                                }}
                                                            />
                                                        </Box>
                                                    ))
                                                )}
                                            </Box>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </Box>
                        </Box>

                        {/* Per-card celebration overlay */}
                        <AnimatePresence mode="sync">
                            {celebration?.childId === child.id && (
                                <TaskCompletionAnimation
                                    key={celebrationKey}
                                    task={celebration.task}
                                    accentColor={celebration.accentColor}
                                    showCoins={celebration.showCoins}
                                    pendingApproval={
                                        celebration.pendingApproval
                                    }
                                    theme={celebration.theme}
                                    userEffect={celebration.userEffect}
                                    onComplete={() => {
                                        setCelebration(null);
                                        if (pendingLevelUp) {
                                            setLevelUp(pendingLevelUp);
                                            setPendingLevelUp(null);
                                        }
                                    }}
                                />
                            )}
                        </AnimatePresence>

                        {/* Weekly allowance treasure chest - shows directly */}
                        <AnimatePresence>
                            {!interactionLocked &&
                                hasUnopenedConversion &&
                                !hasMondayTopUp &&
                                !levelUp && (
                                    <LottieTreasureChest
                                        coinsAwarded={
                                            child.conversionCoins || 0
                                        }
                                        oldBalance={child.coins || 0}
                                        onComplete={() =>
                                            handleMoneyBagComplete(child.id)
                                        }
                                        variant="viikkoraha"
                                        isDark={isDark}
                                    />
                                )}
                        </AnimatePresence>

                        {/* Monday top-up treasure chest - shows directly */}
                        <AnimatePresence>
                            {!interactionLocked &&
                                hasMondayTopUp &&
                                !levelUp && (
                                    <LottieTreasureChest
                                        coinsAwarded={
                                            child.pending_monday_topup || 0
                                        }
                                        oldBalance={child.coins || 0}
                                        onComplete={() =>
                                            handleMondayTopUpComplete(child.id)
                                        }
                                        variant="viikonloppubonus"
                                        isDark={isDark}
                                    />
                                )}
                        </AnimatePresence>

                        {/* Gift reveal animation */}
                        <AnimatePresence>
                            {!interactionLocked &&
                                hasPendingGift &&
                                !hasUnopenedConversion &&
                                !hasMondayTopUp &&
                                !levelUp && (
                                    <LottieGiftReveal
                                        item={child.pendingGift}
                                        coinsAwarded={0}
                                        onComplete={() =>
                                            handleGiftOpened(
                                                child.id,
                                                child.pendingGift
                                                    .notification_id,
                                            )
                                        }
                                        isDark={isDark}
                                    />
                                )}
                        </AnimatePresence>

                        {/* Shared goal reward animation */}
                        <AnimatePresence>
                            {!interactionLocked &&
                                hasPendingGoalReward &&
                                !hasPendingGift &&
                                !hasUnopenedConversion &&
                                !hasMondayTopUp &&
                                !levelUp && (
                                    <LottieGiftReveal
                                        item={
                                            child.pendingGoalReward
                                                .items?.[0] || {
                                                name: "Palkinto",
                                                rarity: "eeppinen",
                                            }
                                        }
                                        coinsAwarded={
                                            child.pendingGoalReward
                                                .coinsPerChild || 0
                                        }
                                        oldBalance={
                                            (child.coins || 0) -
                                            (child.pendingGoalReward
                                                .coinsPerChild || 0)
                                        }
                                        onComplete={() =>
                                            handleGoalRewardOpened(
                                                child.id,
                                                child.pendingGoalReward
                                                    .notification_id,
                                            )
                                        }
                                        isDark={isDark}
                                    />
                                )}
                        </AnimatePresence>

                        {/* Level up overlay */}
                        <AnimatePresence>
                            {levelUp?.childId === child.id && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.4 }}
                                    onClick={() => {
                                        setLevelUp(null);
                                    }}
                                    style={{
                                        position: "absolute",
                                        inset: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        zIndex: 25,
                                        pointerEvents: "auto",
                                        cursor: "pointer",
                                    }}
                                >
                                    <ProfileEffectOverlay
                                        effect={PIXIE_DUST_EFFECT}
                                        fillContainer
                                        zIndex={0}
                                    />
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.5 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.5 }}
                                        transition={{
                                            type: "spring",
                                            stiffness: 300,
                                            damping: 25,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                bgcolor: isDark
                                                    ? "#1e1e2e"
                                                    : "#fff",
                                                borderRadius: "24px",
                                                p: 4,
                                                textAlign: "center",
                                                boxShadow: `0 12px 48px ${colors.accent}66`,
                                                border: `4px solid ${colors.accent}`,
                                                maxWidth: 260,
                                            }}
                                        >
                                            <Typography
                                                variant="h2"
                                                sx={{ mb: 1 }}
                                            >
                                                🎉
                                            </Typography>
                                            <Typography
                                                variant="h5"
                                                fontWeight={900}
                                                sx={{
                                                    color: colors.accent,
                                                    mb: 0.5,
                                                }}
                                            >
                                                Tason nousu!
                                            </Typography>
                                            <Typography
                                                variant="h6"
                                                fontWeight={700}
                                            >
                                                Taso {levelUp.newLevel}:{" "}
                                                {LEVEL_NAMES[
                                                    levelUp.newLevel
                                                ] || ""}
                                            </Typography>
                                        </Box>
                                    </motion.div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Achievement stack - bottom of column */}
                        <AchievementStack
                            achievements={achievementQueues[child.id] || []}
                            onDismiss={() =>
                                setAchievementQueues((prev) => ({
                                    ...prev,
                                    [child.id]: (prev[child.id] || []).slice(1),
                                }))
                            }
                            onAllDismissed={() =>
                                setAchievementStackVisible((prev) => ({
                                    ...prev,
                                    [child.id]: false,
                                }))
                            }
                            isDark={isDark}
                            visible={!!achievementStackVisible[child.id]}
                            accentColor={colors.accent}
                        />

                        {/* Background blur during center overlays */}
                        <AnimatePresence>
                            {(celebration?.childId === child.id ||
                                levelUp?.childId === child.id) && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                    style={{
                                        position: "absolute",
                                        inset: 0,
                                        zIndex: 8,
                                        pointerEvents: "none",
                                    }}
                                >
                                    <GradualBlur
                                        position="top"
                                        strength={3}
                                        height="100%"
                                        divCount={3}
                                        target="parent"
                                        zIndex={8}
                                    />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </Box>
                );
            })}

            {/* Task request dialog */}
            <Dialog
                open={!!taskRequestDialog}
                onClose={() => setTaskRequestDialog(null)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: "24px" } }}
            >
                <DialogTitle sx={{ textAlign: "center", fontWeight: 900 }}>
                    Pyydä lisää tehtäviä 📋
                </DialogTitle>
                <DialogContent>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                    >
                        Vanhempi saa ilmoituksen ja voi lisätä sinulle tehtäviä.
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={2}
                        variant="outlined"
                        label="Viesti (valinnainen)"
                        value={taskRequestMessage}
                        onChange={(e) => setTaskRequestMessage(e.target.value)}
                        placeholder="esim. Haluan siivota huoneeni"
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button onClick={() => setTaskRequestDialog(null)}>
                        Peruuta
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<RequestIcon />}
                        onClick={async () => {
                            const childId = taskRequestDialog;
                            setTaskRequestDialog(null);
                            try {
                                // Need child's token — use kiosk API (parent token is active)
                                await api.post("/tasks/requests", {
                                    message: taskRequestMessage,
                                    userId: childId,
                                });
                                setTaskRequestSent((prev) => ({
                                    ...prev,
                                    [childId]: true,
                                }));
                                setTimeout(
                                    () =>
                                        setTaskRequestSent((prev) => {
                                            const n = { ...prev };
                                            delete n[childId];
                                            return n;
                                        }),
                                    60000,
                                );
                            } catch (e) {
                                console.error("Task request failed", e);
                            }
                        }}
                    >
                        Lähetä pyyntö
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Shield info dialog */}
            <Dialog
                open={shieldInfoOpen}
                onClose={() => {
                    setShieldInfoOpen(false);
                    setShieldChildId(null);
                }}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: "24px" } }}
            >
                <DialogTitle
                    sx={{
                        textAlign: "center",
                        fontWeight: 900,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                    }}
                >
                    <ShieldIcon
                        sx={{ color: statusColors.shield, fontSize: 32 }}
                    />
                    Streak-suoja
                </DialogTitle>
                <DialogContent>
                    {(() => {
                        const child = children.find(
                            (c) => c.id === shieldChildId,
                        );
                        const hasFreeze = child?.freezeAvailable > 0;
                        const freezePrice = child?.freezePrice || 12;
                        const canAfford = (child?.coins || 0) >= freezePrice;

                        if (hasFreeze) {
                            return (
                                <Typography
                                    variant="body1"
                                    sx={{ textAlign: "center" }}
                                >
                                    Sinulla on streak-suoja aktiivisena! Se
                                    suojaa sinua menettämästä streak-bonusta
                                    yhden kerran viikoittain, jos et tee
                                    tehtäviä yhtenä päivänä.
                                </Typography>
                            );
                        } else {
                            return (
                                <Box sx={{ textAlign: "center" }}>
                                    <Typography variant="body1" sx={{ mb: 2 }}>
                                        Sinulla ei ole streak-suojaa. Osta se
                                        nyt suojataksesi streakisi yhdeltä
                                        päivältä ilman tehtäviä!
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 1,
                                            mb: 1,
                                        }}
                                    >
                                        <Typography
                                            variant="h6"
                                            fontWeight={700}
                                        >
                                            {freezePrice} 🪙
                                        </Typography>
                                    </Box>
                                    {!canAfford && (
                                        <Typography
                                            variant="caption"
                                            color="error"
                                            sx={{ display: "block", mt: 1 }}
                                        >
                                            Tarvitset{" "}
                                            {freezePrice - (child?.coins || 0)}{" "}
                                            kolikkoa lisää
                                        </Typography>
                                    )}
                                </Box>
                            );
                        }
                    })()}
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center", pb: 2, gap: 1 }}>
                    {(() => {
                        const child = children.find(
                            (c) => c.id === shieldChildId,
                        );
                        const hasFreeze = child?.freezeAvailable > 0;
                        const freezePrice = child?.freezePrice || 12;
                        const canAfford = (child?.coins || 0) >= freezePrice;

                        if (!hasFreeze) {
                            return (
                                <>
                                    <Button
                                        onClick={() =>
                                            handleBuyFreeze(shieldChildId)
                                        }
                                        disabled={
                                            !canAfford || purchasingFreeze
                                        }
                                        variant="contained"
                                        sx={{
                                            borderRadius: 50,
                                            bgcolor: canAfford
                                                ? "#00bcd4"
                                                : "#ccc",
                                            boxShadow: canAfford
                                                ? "0px 3px 0px 0px #0097a7"
                                                : "none",
                                            "&:hover": {
                                                bgcolor: canAfford
                                                    ? "#0097a7"
                                                    : "#ccc",
                                                transform: canAfford
                                                    ? "translateY(-2px)"
                                                    : "none",
                                                boxShadow: canAfford
                                                    ? "0px 5px 0px 0px #00838f"
                                                    : "none",
                                            },
                                            "&:active": {
                                                transform: canAfford
                                                    ? "translateY(2px)"
                                                    : "none",
                                                boxShadow: canAfford
                                                    ? "0px 1px 0px 0px #0097a7"
                                                    : "none",
                                            },
                                            "&:disabled": {
                                                bgcolor: "#ccc",
                                                color: "#999",
                                            },
                                            transition:
                                                "transform 0.1s ease, box-shadow 0.1s ease",
                                        }}
                                    >
                                        {purchasingFreeze
                                            ? "Ostetaan..."
                                            : "Osta nyt"}
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            setShieldInfoOpen(false);
                                            setShieldChildId(null);
                                        }}
                                        variant="outlined"
                                        sx={{
                                            borderRadius: 50,
                                            borderColor: statusColors.shield,
                                            color: statusColors.shield,
                                        }}
                                    >
                                        Sulje
                                    </Button>
                                </>
                            );
                        } else {
                            return (
                                <Button
                                    onClick={() => {
                                        setShieldInfoOpen(false);
                                        setShieldChildId(null);
                                    }}
                                    variant="contained"
                                    sx={{
                                        borderRadius: 50,
                                        bgcolor: statusColors.shield,
                                        boxShadow: "0px 3px 0px 0px #0097a7",
                                        "&:hover": {
                                            bgcolor: "#0097a7",
                                            transform: "translateY(-2px)",
                                            boxShadow:
                                                "0px 5px 0px 0px #00838f",
                                        },
                                        "&:active": {
                                            transform: "translateY(2px)",
                                            boxShadow:
                                                "0px 1px 0px 0px #0097a7",
                                        },
                                        transition:
                                            "transform 0.1s ease, box-shadow 0.1s ease",
                                    }}
                                >
                                    Selvä!
                                </Button>
                            );
                        }
                    })()}
                </DialogActions>
            </Dialog>

            {/* Global pulse animation for money bag */}
            <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.03); }
        }
      `}</style>

            {/* Parent: task drawer (tablet) */}
            {user?.type === "parent" && (
                <TaskFormDrawer
                    open={taskDrawerOpen}
                    onClose={() => setTaskDrawerOpen(false)}
                    onSuccess={loadData}
                    anchor="right"
                />
            )}

            {/* Undo completion confirmation dialog */}
            <Dialog
                open={!!undoConfirm}
                onClose={() => setUndoConfirm(null)}
                PaperProps={{ sx: { borderRadius: 4, p: 1 } }}
            >
                <DialogTitle sx={{ textAlign: "center", fontWeight: 900 }}>
                    Peru tehtävä?
                </DialogTitle>
                <DialogContent>
                    <Typography align="center">
                        Perutaanko tehtävä "{undoConfirm?.taskTitle}"?
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                        sx={{ mt: 1 }}
                    >
                        Pisteet ja mahdolliset kolikot palautetaan.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center", pb: 3, gap: 1 }}>
                    <Button
                        onClick={() => setUndoConfirm(null)}
                        variant="outlined"
                        sx={{ borderRadius: 3, px: 3 }}
                    >
                        Peruuta
                    </Button>
                    <Button
                        onClick={confirmUndoCompletion}
                        variant="contained"
                        color="error"
                        sx={{ borderRadius: 3, px: 3 }}
                    >
                        Peru tehtävä
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default KioskDashboard;
